import { render, html, nothing } from 'lit-html';
import {
  getInventory,
  getInventoryFilterData,
  getInventoryDateRange,
  toISODate,
  getInventoryTypeSeasonPairs,
  variantInventoryKeys,
  isDeliveryWindowPast,
} from 'b2b-plus/inventory';
import {
  toastNotification,
} from 'b2b-plus/ui';
import {
  getCart,
  addToCart,
  migrateCart,
  getProductRecord,
  saveProductRecord,
  isProductRecordIncomplete,
  getProductJSON,
  enrichProductJSONWithSeasonPrices,
  clearCart,
  manageCartSwitch,
  ensureOrderContext,
  runPendingSavedOrderMigration,
  findInventoryExceededIssues,
  B2B_PLUS_CLIENT_TAB_ID,
  B2B_CART_SAVED_DEBOUNCE_MS,
  getAllStoredDeliveryWindows,
  b2bPlusString,
  optionLabel,
  inferSizeOptionIndex,
  resetCartStorageConnection,
  describeCartLine,
  uniqueLineDescriptors,
} from 'b2b-plus/utils';
import {
  cartLoadingTemplate,
  cartEmptyItemsTemplate,
  cartLoadErrorTemplate,
  cartMessageWithHandlesTemplate,
  cartMessageWithLinesTemplate,
  cartIssuesBarTemplate,
} from 'b2b-plus/ui-templates';
import { cartLockConfig } from 'b2b-plus/cart-lock';
import {
  isPoNumberEnabled,
  poNumberScope,
  getPoFromSnapshot,
  getGlobalPoFromSnapshot,
  computePoSyncWrites,
  computePoSyncWritesGlobal,
  applyPoToRecord,
  parseStorageKey,
  sumCartItems,
  collectDeliveryWindowsFromCart,
  computeCartTotals,
  computeShipmentTotals,
  meetsMinimumOrderValuePerShipment,
  expandValidProducts,
  variantIdsFromCartItems,
  buildInventoryDateRange,
  hasSplitOptionGroups,
  deriveLockedType,
  deliveryWindowsChanged,
  computeChangedShippingWindows,
  numberCartIssues,
  countCartIssuesByCode,
  cartIssueKey,
} from 'b2b-plus/cart-sdk';
import {
  saveShipmentShippingForWindow,
  clearCheckoutShipmentShipping,
  getCartProductLookups,
  getSharedShipmentDates,
  setSharedShipmentDate,
  updateDeliveryWindowsForOrderTypeGroup,
  LOCKED_SHIPMENT_WINDOWS_ROLLED_EVENT,
} from 'b2b-plus/cart-storage';
import {
  joinDeliveryWindow,
  isPrePopulatedShipmentDateEnabled,
  isLockedShipmentWindowEnabled,
  unitCentsForLineProperties,
} from 'b2b-plus/product-table-sdk';

/**
 * Displayed unit price for a cart line. Future / Prebook use that variant's
 * season metafield when the ship-date start is outside today's season window;
 * the current season and every other type use wholesale.
 * @param {object} productJSON
 * @param {object} item
 * @returns {number}
 */
function displayedUnitCents(productJSON, item) {
  const variant = (productJSON?.variants ?? []).find(
    (v) => String(v.id) === String(item?.id),
  );
  return unitCentsForLineProperties(variant, item?.properties);
}

/*
 * Banner kinds sharing `[data-cart-errors]`, lower renders first. Every banner goes
 * through the registry (`#setBanner`) so one can no longer erase another.
 * Per-line checkout problems (stock, dates, quantities) are NOT banners: they are
 * checkout issues, listed on the sticky strip and pinned to their cells.
 */
const CART_BANNER_PRIORITY = {
  'unavailable-removed':            1,
  'checkout-incomplete':            4,
  'checkout-inventory-unavailable': 4,
};
/** Cleared at the start of every checkout attempt; the rest persist for the load. */
const CART_CHECKOUT_BANNERS = ['checkout-incomplete', 'checkout-inventory-unavailable'];

/**
 * Hand the thread back to the browser so it can paint and process input between
 * chunks of a long cart-wide operation.
 * @returns {Promise<void>}
 */
const _yieldToBrowser = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

class B2BPlusCart extends HTMLElement {

  // ── Private fields ───────────────────────────────────────────────────────────
  /** @type {ShadowRoot} Initialised in constructor. */
  #shadowRoot = this.attachShadow({ mode: 'closed' });
  /** @type {object[]|null} All valid product JSON objects (full cart, not just rendered batch). */
  #validProducts          = [];
  /** @type {object|null} Raw IndexedDB cart data (handle → cartItem[]). */
  #cartData               = null;
  /** @type {object|null} Inventory data keyed by product id. */
  #inventoryData          = null;
  /** @type {object|null} Filter options derived from inventory. */
  #inventoryFilterOptions = null;
  /** @type {number} Number of products already rendered to the DOM. */
  #loadedOffset           = 0;
  /** @type {IntersectionObserver|null} Watches the load-more sentinel. */
  #sentinelObserver       = null;
  /** @type {{ date: string, season: string, type: string }|null} */
  #activeFilters          = null;
  /**
   * Ordered list of <b2b-plus-product-table> elements currently in the
   * cart DOM.  Maintained by #renderNextBatch / #prependProductTable /
   * #removeProductTable / #loadCartItems so #applyFilters never needs to
   * call querySelectorAll('b2b-plus-product-table') on filter changes.
   * @type {HTMLElement[]}
   */
  #filterTableCache       = [];
  /**
   * Per-table row and date-picker element references.  Populated lazily on
   * the first #applyFilters call that reaches a given table.
   * @type {Map<HTMLElement, { rows: HTMLElement[], datePickers: Map<HTMLElement, HTMLElement|null> }>}
   */
  #filterRowCache         = new Map();
  /** @type {boolean} */
  #isValidatingInventory  = false;
  /** @type {Map<string, import('lit-html').TemplateResult>} Banner kind → template; see CART_BANNER_PRIORITY. */
  #banners                = new Map();
  /**
   * Checkout issues by the pass that raised them — per-table DOM validation, the
   * storage-wide pass (`records`: past-dated rows of tables not rendered yet) and the
   * fresh-inventory check. All are reset at the start of every checkout attempt; an
   * issue also drops out once the buyer edits the cell it is pinned to.
   * `load` holds what the saved order already got wrong when the cart opened (see
   * #scanLoadIssues); it is replaced by the checkout passes on the next attempt.
   * @type {{ dom: object[], records: object[], inventory: object[], load: object[] }}
   */
  #issueSources           = { dom: [], records: [], inventory: [], load: [] };
  /** @type {object[]} All sources merged and numbered in reading order (see numberCartIssues). */
  #issues                 = [];
  /** @type {number} Index into `#issues` of the last issue "Next issue" jumped to. */
  #issueCursor            = -1;
  /** @type {boolean} "Show only issues" switch on the issue strip. */
  #issuesOnly             = false;
  /** @type {Element|null} Sticky issue strip (`[data-cart-issues-bar]`). */
  #issuesBarEl            = null;
  /** @type {(() => void)|null} Scroll handler keeping the strip below the sticky theme header. */
  #issuesBarOffsetHandler = null;
  // Refs set in connectedCallback after shadow DOM is stamped:
  /** @type {Element|null} */ #cartItemsContainer = null;
  /** @type {Element|null} */ #filterEmptyState   = null;
  /** @type {Element|null} */ #cartErrorsEl       = null;
  /** @type {Element|null} */ #cartHeaderEl       = null;
  /** @type {Element|null} */ #cartFooterEl       = null;
  /**
   * @type {Element|null} The `[data-cart-footer]` wrapper around `<b2b-plus-cart-footer>`.
   * Hidden while the cart is empty so the totals / subtotal / checkout block does not
   * render zeroes next to the empty-cart message.
   */
  #cartFooterWrapEl = null;
  /** @type {ReturnType<typeof setTimeout>|null} Debounces footer refresh on `b2b:cart-saved`. */
  #cartSavedDebounceTimer = null;
  /** @type {Promise<void>|null} In-flight `#refreshCartSummary` run; serializes overlapping refreshes. */
  #refreshSummaryInFlight = null;
  /** @type {boolean} A refresh was requested while another was in flight; triggers one re-run. */
  #refreshSummaryQueued = false;
  /** @type {Element|null} The [data-po-box-field] container inside the shadow DOM. */
  #poBoxEl = null;
  /** @type {Element|null} The [data-clear-cart-action] container inside the shadow DOM. */
  #clearCartActionEl = null;
  /** @type {boolean} Prevents duplicate clear-cart submissions. */
  #isClearingCart = false;
  /** @type {boolean} True while cart data is being fetched/rendered; keeps the clear-cart button disabled. */
  #cartIsLoading = false;
  /** @type {Promise<void>|null} In-flight #loadCartItems run; serializes overlapping loads. */
  #loadInFlight = null;
  /** @type {Promise<void>|null} In-flight abandoned-checkout import; overlapping loads must not double-add. */
  #restoreAbandonedPromise = null;
  /** @type {boolean} A load was requested while another was in flight; triggers one re-run. */
  #loadQueued = false;
  /** @type {BroadcastChannel|null} Listens for cart writes from other tabs (same `IndexedDB`). */
  #cartBroadcast = null;
  /** @type {ReturnType<typeof setTimeout>|null} Debounces full cart reload after cross-tab updates. */
  #crossTabCartReloadTimer = null;
  /**
   * List vs compact grid for cart line items (products).
   * Only used when `config.cart.enableCartLineLayoutToggle` is true.
   * @type {'list'|'grid'}
   */
  #cartLineLayout = 'list';
  /** @type {Element|null} */
  #layoutToggleRoot = null;
  /** @type {HTMLElement|null} Full-screen loader overlay shown while opening checkout / refreshing the cart. */
  #pageLoaderEl = null;
  /** @type {boolean} window `pageshow` listener for bfcache restore. */
  #pageShowBound = false;
  /** @type {ReturnType<typeof setTimeout>|null} Watchdog reload if bfcache cart reload hangs. */
  #bfcacheReloadTimer = null;

  #onCartLayoutToggleClick = (e) => {
    const btn = e.target.closest('[data-cart-layout]');
    if (!btn || !this.#layoutToggleRoot?.contains(btn)) return;
    const layout = btn.dataset.cartLayout;
    if (layout === 'list' || layout === 'grid') this.#setCartLineLayout(layout);
  };

  #onCartEditQtyClick = (e) => {
    const btn = e.target.closest('[data-cart-edit-qty]');
    if (!btn || !this.#cartItemsContainer?.contains(btn)) return;
    e.preventDefault();
    const slot = btn.closest('[data-b2b-plus-cart-line-slot]');
    this.#setCartLineLayout('list');
    requestAnimationFrame(() => {
      slot?.querySelector('b2b-plus-product-table')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  #onCartGridRemoveClick = async (e) => {
    const btn = e.target.closest('[data-cart-grid-remove]');
    if (!btn || !this.#cartItemsContainer?.contains(btn)) return;
    e.preventDefault();
    if (btn.disabled) return;

    const slot   = btn.closest('[data-b2b-plus-cart-line-slot]');
    const table  = slot?.querySelector('b2b-plus-product-table');
    const handle = table?.dataset.productHandle;
    if (!handle) return;

    if (!window.confirm(b2bPlusString('removeFromOrderConfirm'))) return;

    const spinner = btn.querySelector('[data-remove-spinner]');
    const btnText = btn.querySelector('[data-remove-btn-text]');

    btn.disabled = true;
    spinner?.removeAttribute('hidden');
    if (btnText) btnText.hidden = true;

    try {
      await clearCart(handle);
      this.#removeProductTable(handle);
    } catch (err) {
      console.error('b2b-plus: grid remove failed', err);
      btn.disabled = false;
      spinner?.setAttribute('hidden', '');
      if (btnText) btnText.hidden = false;
    }
  };

  /**
   * lit-html template for the `.b2b-plus-cart-line-slot` card chrome used in grid layout.
   * Renders the product table element followed by the Edit Quantity and Remove buttons.
   * Passing a live DOM node as a template value moves it into the rendered position.
   *
   * @param {HTMLElement} tableEl - The `<b2b-plus-product-table>` element for this cart line.
   * @returns {import('lit-html').TemplateResult}
   */
  #cartLineSlotActionsTemplate(tableEl) {
    return html`
      ${tableEl}
      <div class="b2b-plus-cart-line-slot-actions">
        <button type="button" class="b2b-plus-cart-edit-qty-btn" data-cart-edit-qty>
          ${b2bPlusString('cartEditQuantity')}
        </button>
        <button
          type="button"
          class="b2b-plus-link b2b-plus-remove-btn b2b-plus-cart-grid-remove-btn"
          data-cart-grid-remove
        >
          <span data-remove-btn-text>
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
              <path d="M16.625 3.5625H3.5625L4.75 17.8125H14.25L15.4375 3.5625H2.375M9.5 7.125V14.25M12.4688 7.125L11.875 14.25M6.53125 7.125L7.125 14.25M7.125 2.77083L7.71875 1.1875H11.2813L11.875 2.77083" stroke="#941B1E" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/>
            </svg>
          </span>
          <div class="b2b-plus-spinner" data-remove-spinner hidden>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 66 66" aria-hidden="true">
              <circle stroke-width="6" cx="33" cy="33" r="30" fill="none"></circle>
            </svg>
          </div>
        </button>
      </div>
    `;
  }

  /** Shadow + light-DOM bridge listeners; removed in disconnectedCallback so reconnect does not duplicate. */
  #cartBridgeListenersBound = false;
  /** @type {HTMLButtonElement|null} */
  #clearCartBtn = null;

  #onShadowCartFilter = (e) => this.#applyFilters(e.detail);

  #onShadowCartBulkApply = (e) =>
    this.#onCartBulkApplyFromTable(e.detail).catch((err) =>
      console.error('b2b-plus-cart: cart-wide bulk apply failed', err),
    );

  #onShadowMultiApplyDate = (e) => {
    const { start, end, scope } = e.detail ?? {};
    if (!start || !end) return;
    this.#filterTableCache.forEach((table) => table.applyDeliveryDate?.(start, end, scope ?? 'all'));

    // Keep shared-per-type defaults + IDB `requested_ship_date` aligned with multi-apply.
    void this.#syncSharedShipmentDatesFromMultiApply(start, end);

    // After applying, switch the date-filter tab to "All dates" so the user
    // can see every updated row regardless of which tab was active before.
    // Clicking the button reuses the header's own handler: it removes
    // is-active from all tabs, marks "all" active, and fires b2b:cart-filter.
    const allTab = this.#cartHeaderEl?.querySelector('[data-tab="all"]');
    allTab?.click();
  };

  #onSharedShipmentDateChanged = (e) => {
    const { group, start, end } = e.detail ?? {};
    if (!group || !start || !end) return;
    const sourceTable = e.target?.closest?.('b2b-plus-product-table') ?? e.target;
    this.#filterTableCache.forEach((table) => {
      if (table === sourceTable) return;
      table.applySharedShipmentDate?.(start, end, group);
    });
  };

  /**
   * After cart multi-apply: persist the applied window as the shared default for
   * every enabled inventory-type group, amend IDB rows (so `requested_ship_date`
   * stays correct for products not currently mounted), and warn when amending.
   * @param {string} startISO
   * @param {string} endISO
   * @returns {Promise<void>}
   */
  async #syncSharedShipmentDatesFromMultiApply(startISO, endISO) {
    const nextWindow = joinDeliveryWindow(startISO, endISO);
    if (!nextWindow) return;

    const groups = [
      { group: 'immediate', probeType: 'ats' },
      { group: 'future', probeType: 'future' },
      { group: 'prebook', probeType: 'prebook' },
    ].filter(({ probeType }) => (
      isPrePopulatedShipmentDateEnabled(probeType)
      && !isLockedShipmentWindowEnabled(probeType)
    ));

    if (!groups.length) return;

    let previous;
    try { previous = await getSharedShipmentDates(); }
    catch { previous = {}; }

    let anyAmend = false;
    for (const { group } of groups) {
      const prev = String(previous[group] ?? '').trim();
      if (prev && prev !== nextWindow) anyAmend = true;
      try {
        await setSharedShipmentDate(group, nextWindow);
        await updateDeliveryWindowsForOrderTypeGroup(group, startISO, endISO, {
          onlyIfHasDate: true,
        });
      } catch (err) {
        console.error('b2b-plus-cart: shared shipment date multi-apply sync failed', err);
      }
    }

    if (anyAmend) {
      toastNotification(
        b2bPlusString('sharedShipmentDateAmended')
          || 'Previously selected products will also have their ship date amended.',
        'warning',
      );
    }
  }

  #onShadowCartSaved = (event) => {
    const table = event.target.closest?.('b2b-plus-product-table');
    table?.classList.add('updating--data');
    if (table) this.#filterRowCache.delete(table);
    clearTimeout(this.#cartSavedDebounceTimer);
    // Debounce window is shared with the producer in product-table.js to absorb
    // the burst of saves emitted while #validateRestoredData runs during its
    // first connectedCallback. Constant lives in b2b-plus-cart-storage so the
    // two files cannot drift. See B2B_CART_SAVED_DEBOUNCE_MS docstring.
    this.#cartSavedDebounceTimer = setTimeout(() => {
      this.#cartSavedDebounceTimer = null;
      // `#refreshCartSummary` always clears `updating--data` in its finally.
      // Catch here so a rejected refresh is still logged when the caller does
      // not await (setTimeout fire-and-forget).
      this.#refreshCartSummary().catch((err) =>
        console.error('b2b-plus-cart: refreshCartSummary failed after cart-saved', err),
      );
    }, B2B_CART_SAVED_DEBOUNCE_MS);
  };

  /**
   * Remove the `updating--data` overlay (`opacity` + `pointer-events: none`) from
   * every cart product table. Clears both the filter cache and any live DOM
   * matches so a table that left the cache cannot stay permanently inert.
   */
  #clearUpdatingDataClass() {
    this.#filterTableCache.forEach((table) => table.classList.remove('updating--data'));
    this.#cartItemsContainer
      ?.querySelectorAll('b2b-plus-product-table.updating--data')
      .forEach((table) => table.classList.remove('updating--data'));
  }

  #onQuickAddAdded = async (event) => {
    const { productJSON, inventoryData, cartItems } = event.detail ?? {};
    if (productJSON && inventoryData) {
      // Split-option products: #prependProductTable would need to create and correctly
      // key N option-group tables. A full reload is simpler and guaranteed correct.
      if (this.#hasSplitOptionGroups(productJSON)) {
        this.#loadCartItems();
        return;
      }
      await this.#prependProductTable(productJSON, inventoryData, cartItems ?? []);
    } else {
      this.#loadCartItems();
    }
  };

  #onProductRemovedFromHost = (event) => {
    const { handle } = event.detail ?? {};
    if (handle) this.#removeProductTable(handle);
  };

  #onCrossTabCartMessage = (ev) => {
    const { type, sourceTab } = ev.data ?? {};
    if (type !== 'cart-updated') return;
    if (sourceTab === B2B_PLUS_CLIENT_TAB_ID) return;
    clearTimeout(this.#crossTabCartReloadTimer);
    this.#crossTabCartReloadTimer = setTimeout(() => {
      this.#crossTabCartReloadTimer = null;
      this.#filterRowCache.clear();
      this.#loadCartItems();
    }, 400);
  };

  #onLockedShipmentWindowsRolled = () => {
    this.#filterRowCache.clear();
    this.#loadCartItems();
  };

  /**
   * A product table just made an inline error visible (validation, live clamp,
   * inventory refresh, date correction — the table does not say which). The compact
   * grid layout hides every line's inventory matrix, so the error would render into
   * a hidden region: leave grid for list view so the offending table is on screen.
   * The table has already flipped its own summary view to edit for the same reason.
   */
  #onTableErrorShown = () => {
    if (this.#cartLineLayout !== 'grid' || !this.#isCartLineLayoutToggleEnabled()) return;
    this.#setCartLineLayout('list');
  };

  #onClearCartBtnClick = () => this.#onClearCartClicked();

  // ── Banners (`[data-cart-errors]`) ─────────────────────────────────────────

  /**
   * Add, replace or (with `null`) remove one banner, then re-render them all.
   * @param {keyof typeof CART_BANNER_PRIORITY} kind
   * @param {import('lit-html').TemplateResult|null} template
   */
  #setBanner(kind, template) {
    if (template) this.#banners.set(kind, template);
    else this.#banners.delete(kind);
    this.#renderBanners();
  }

  /** Drop the banners owned by a checkout attempt; load-scoped ones stay. */
  #clearCheckoutBanners() {
    for (const kind of CART_CHECKOUT_BANNERS) this.#banners.delete(kind);
    this.#renderBanners();
  }

  /** The single render into `[data-cart-errors]`: every banner, priority-sorted. */
  #renderBanners() {
    const errorsEl = this.#cartErrorsEl;
    if (!errorsEl) return;
    const kinds = [...this.#banners.keys()].sort(
      (a, b) => (CART_BANNER_PRIORITY[a] ?? 99) - (CART_BANNER_PRIORITY[b] ?? 99),
    );
    render(html`${kinds.map((kind) => this.#banners.get(kind))}`, errorsEl);
    errorsEl.hidden = !kinds.length;
  }

  // ── Checkout issues (sticky strip + per-product issue lists) ──────────────

  /**
   * The strip's container. Rendered by the section template; created here (just above
   * the items list) when a customised template predates it.
   * @returns {Element|null}
   */
  #ensureIssuesBarEl() {
    const existing = this.#shadowRoot.querySelector('[data-cart-issues-bar]');
    if (existing) return existing;
    const items = this.#shadowRoot.querySelector('[data-cart-items-list]');
    if (!items) return null;
    const bar = document.createElement('div');
    bar.className = 'b2b-plus-issues-bar';
    bar.setAttribute('data-cart-issues-bar', '');
    bar.hidden = true;
    items.before(bar);
    return bar;
  }

  /**
   * Replace one pass's issues and re-publish.
   * @param {'dom'|'records'|'inventory'} source
   * @param {object[]} issues
   */
  #setIssueSource(source, issues) {
    this.#issueSources[source] = Array.isArray(issues) ? issues : [];
    this.#publishIssues();
  }

  /** Drop every issue (new checkout attempt, cart reload). */
  #clearCartIssues() {
    this.#issueSources = { dom: [], records: [], inventory: [], load: [] };
    this.#publishIssues();
  }

  /**
   * Drop issues matching `predicate` from every source and re-publish when any went.
   * @param {(issue: object) => boolean} predicate
   */
  #dropIssues(predicate) {
    let changed = false;
    const next = {};
    for (const [source, issues] of Object.entries(this.#issueSources)) {
      next[source] = issues.filter((i) => !predicate(i));
      if (next[source].length !== issues.length) changed = true;
    }
    if (!changed) return;
    this.#issueSources = next;
    this.#publishIssues();
  }

  /** @param {string} handle */
  #issuesForHandle(handle) {
    return this.#issues.filter((issue) => issue.handle === handle);
  }

  /**
   * Merge + number every source, hand each rendered table its slice, and refresh the
   * strip and the footer's "Resolve N issues" line. Tables rendered later pick up their
   * slice in #renderNextBatch / #prependProductTable.
   */
  #publishIssues() {
    const products = this.#validProducts ?? [];
    const columns  = new Map(products.map((p) => [
      p.handle,
      new Map((p.variants ?? []).map((v, i) => [String(v.id), i])),
    ]));
    const { dom, records, inventory, load } = this.#issueSources;
    const merged = [...dom, ...records, ...inventory, ...load].map((issue) => ({
      ...issue,
      columnIndex: columns.get(issue.handle)?.get(String(issue.variantId ?? '')) ?? -1,
    }));
    this.#issues      = numberCartIssues(merged, products.map((p) => p.handle));
    this.#issueCursor = -1;

    this.#cartItemsContainer?.querySelectorAll('b2b-plus-product-table').forEach((table) => {
      table.setCartIssues?.(this.#issuesForHandle(table.dataset.productHandle ?? ''));
    });
    this.#renderIssuesBar();
    this.#cartFooterEl?.setIssueCount?.(this.#issues.length);
  }

  /**
   * Past-dated rows straight from storage, as `date-past` issues — one per ordered row
   * (qty > 0) whose delivery window starts before today. Render-independent, so it
   * covers the products on pages the buyer has not scrolled to.
   * @param {{ skipRendered?: boolean }} [opts] - Skip products whose table is rendered
   *   (their DOM validation already reported the same rows, keyed by row id).
   * @returns {Promise<object[]>}
   */
  async #pastDatedRecordIssues({ skipRendered = false } = {}) {
    const rendered = new Set(
      [...(this.#cartItemsContainer?.querySelectorAll('b2b-plus-product-table') ?? [])]
        .map((t) => t.dataset.productHandle ?? ''),
    );
    const message = b2bPlusString('deliveryDatePastError') || 'The selected delivery date has passed.';
    const issues = [];
    for (const product of this.#validProducts ?? []) {
      if (skipRendered && rendered.has(product.handle)) continue;
      let record;
      try { record = await getProductRecord(product.handle); }
      catch { continue; }
      for (const [storageKey, combo] of Object.entries(record ?? {})) {
        for (const row of Array.isArray(combo?.rows) ? combo.rows : []) {
          const win = String(row?.deliveryWindow ?? '').trim();
          if (!win || !isDeliveryWindowPast(win)) continue;
          if (!Object.values(row.byVariantId ?? {}).some((q) => Number(q) > 0)) continue;
          issues.push({
            ...describeCartLine({
              productJSON: product,
              handle:      product.handle,
              properties:  { ...combo, requested_ship_date: win },
            }),
            variantId:      '',
            rowId:          String(row.rowId ?? ''),
            combinationKey: storageKey,
            code:           'date-past',
            message,
          });
        }
      }
    }
    return issues;
  }

  /**
   * Issues the saved order already has when the cart opens, so the buyer sees them
   * without first pressing checkout: every stock problem against the inventory just
   * loaded (same pass as checkout, see `findInventoryExceededIssues`) plus ordered
   * lines whose delivery date has gone by.
   *
   * Reads the storage snapshot taken at load — BEFORE the tables clamp over-stock
   * quantities — so "Requested 6, only 4 available" still reports what was asked for.
   * @param {object[]}    validProducts
   * @param {object|null} cartData      - `getCart()` snapshot (storage key → line items).
   * @param {object|null} inventoryData
   * @returns {object[]}
   */
  #scanLoadIssues(validProducts, cartData, inventoryData) {
    const issues = [];
    if (inventoryData?.inventories) {
      try {
        const { invalidItems } = findInventoryExceededIssues({
          cartData,
          products:           validProducts,
          freshInventoryData: inventoryData,
        });
        issues.push(...invalidItems.map((item) => ({ ...item, code: item.code ?? 'out-of-stock' })));
      } catch (err) {
        console.warn('b2b-plus: load-time issue scan failed', err);
      }
    }
    const pastMsg = b2bPlusString('deliveryDatePastError') || 'The selected delivery date has passed.';
    for (const product of validProducts) {
      for (const item of Array.isArray(cartData?.[product.handle]) ? cartData[product.handle] : []) {
        if (Number(item?.quantity) <= 0) continue;
        const p   = item.properties ?? {};
        const win = String(p.requested_ship_date ?? '').trim();
        if (!win || !isDeliveryWindowPast(win)) continue;
        // One per row (window): no variantId, so numberCartIssues collapses the row's lines.
        issues.push({
          ...describeCartLine({ productJSON: product, handle: product.handle, properties: p }),
          variantId: '',
          code:      'date-past',
          message:   pastMsg,
        });
      }
    }
    return issues;
  }

  /** Render (or hide) the sticky issue strip. */
  #renderIssuesBar() {
    const bar = this.#issuesBarEl;
    if (!bar) return;
    const issues = this.#issues;
    if (!issues.length) {
      render(nothing, bar);
      bar.hidden = true;
      this.#setIssuesOnly(false);
      this.#unbindIssuesBarOffset();
      return;
    }
    render(cartIssuesBarTemplate({
      total:        issues.length,
      productCount: new Set(issues.map((i) => i.handle)).size,
      productTotal: (this.#validProducts ?? []).length,
      chips:        countCartIssuesByCode(issues),
      onlyIssues:   this.#issuesOnly,
    }), bar);
    bar.hidden = false;
    this.#bindIssuesBarOffset();
  }

  /** @param {boolean} on */
  #setIssuesOnly(on) {
    this.#issuesOnly = Boolean(on);
    this.#cartItemsContainer?.toggleAttribute('data-cart-issues-only', this.#issuesOnly);
  }

  /**
   * Keep the sticky strip just below the theme's sticky header. The header shrinks /
   * hides as the page scrolls, so its live bottom edge is read per frame rather than a
   * fixed offset. Only bound while the strip is showing.
   */
  #bindIssuesBarOffset() {
    if (this.#issuesBarOffsetHandler) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const header = document.querySelector('.shopify-section-header');
      const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      this.#issuesBarEl?.style.setProperty('--b2b-plus-issues-bar-top', `${bottom}px`);
    };
    this.#issuesBarOffsetHandler = () => { if (!frame) frame = requestAnimationFrame(update); };
    window.addEventListener('scroll', this.#issuesBarOffsetHandler, { passive: true });
    window.addEventListener('resize', this.#issuesBarOffsetHandler, { passive: true });
    update();
  }

  #unbindIssuesBarOffset() {
    if (!this.#issuesBarOffsetHandler) return;
    window.removeEventListener('scroll', this.#issuesBarOffsetHandler);
    window.removeEventListener('resize', this.#issuesBarOffsetHandler);
    this.#issuesBarOffsetHandler = null;
  }

  /** @param {string} handle */
  #findProductTable(handle) {
    return this.#cartItemsContainer?.querySelector(
      `b2b-plus-product-table[data-product-handle="${CSS.escape(handle)}"]`,
    ) ?? null;
  }

  /**
   * The table for `handle`, rendering further batches first when it sits on a page the
   * buyer has not scrolled to yet.
   * @param {string} handle
   * @returns {Promise<HTMLElement|null>}
   */
  async #ensureProductTableRendered(handle) {
    let table = this.#findProductTable(handle);
    while (!table && this.#loadedOffset < (this.#validProducts ?? []).length) {
      const before = this.#loadedOffset;
      this.#renderNextBatch();
      if (this.#loadedOffset <= before) break;
      await _yieldToBrowser();
      table = this.#findProductTable(handle);
    }
    return table;
  }

  /**
   * Scroll to issue `index` (into `#issues`): back to list layout if needed (the grid
   * hides the matrix), clear a date filter that hides it, render its page, then let the
   * table scroll to / focus the cell.
   * @param {number} index
   */
  async #goToIssue(index) {
    const issue = this.#issues[index];
    if (!issue) return;
    this.#issueCursor = index;
    if (this.#cartLineLayout === 'grid' && this.#isCartLineLayoutToggleEnabled()) {
      this.#setCartLineLayout('list');
      await _yieldToBrowser();
    }
    const table = await this.#ensureProductTableRendered(issue.handle);
    if (!table) return;
    await table.whenReady?.();
    if (table.hidden || table.closest('[hidden]')) {
      this.#cartHeaderEl?.querySelector('[data-tab="all"]')?.click();
      await _yieldToBrowser();
    }
    if (!table.focusCartIssue?.(issue.number)) {
      table.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /** Jump to the first issue — end of a failed checkout attempt. */
  #revealFirstIssue() {
    if (this.#issues.length) this.#goToIssue(0);
  }

  #onIssuesResolved = (event) => {
    const numbers = new Set(event.detail?.numbers ?? []);
    if (!numbers.size) return;
    const keys = new Set(this.#issues.filter((i) => numbers.has(i.number)).map(cartIssueKey));
    this.#dropIssues((issue) => keys.has(cartIssueKey(issue)));
  };

  #onIssuesBarClick = (event) => {
    const next = event.target.closest?.('[data-cart-issues-next]');
    if (next) {
      if (this.#issues.length) this.#goToIssue((this.#issueCursor + 1) % this.#issues.length);
      return;
    }
    const chip = event.target.closest?.('[data-cart-issues-chip]');
    if (chip) {
      const index = this.#issues.findIndex((i) => i.code === chip.dataset.cartIssuesChip);
      if (index >= 0) this.#goToIssue(index);
    }
  };

  #onIssuesBarChange = (event) => {
    if (event.target.matches?.('[data-cart-issues-only]')) this.#setIssuesOnly(event.target.checked);
  };

  #bindCartBridgeListeners() {
    if (this.#cartBridgeListenersBound) return;
    this.#cartBridgeListenersBound = true;
    const sr = this.#shadowRoot;
    sr.addEventListener('b2b:cart-filter', this.#onShadowCartFilter);
    sr.addEventListener('b2b:cart-bulk-apply', this.#onShadowCartBulkApply);
    sr.addEventListener('b2b:multi-apply-date', this.#onShadowMultiApplyDate);
    sr.addEventListener('b2b:shared-shipment-date-changed', this.#onSharedShipmentDateChanged);
    sr.addEventListener('b2b:cart-saved', this.#onShadowCartSaved);
    sr.addEventListener('b2b:table-error-shown', this.#onTableErrorShown);
    sr.addEventListener('b2b:cart-issues-resolved', this.#onIssuesResolved);
    this.#issuesBarEl?.addEventListener('click', this.#onIssuesBarClick);
    this.#issuesBarEl?.addEventListener('change', this.#onIssuesBarChange);

    this.addEventListener('b2b:quick-add-added', this.#onQuickAddAdded);
    this.addEventListener('b2b:product-removed', this.#onProductRemovedFromHost);
    this.#clearCartBtn = this.#clearCartActionEl?.querySelector('[data-clear-cart-btn]') ?? null;
    this.#clearCartBtn?.addEventListener('click', this.#onClearCartBtnClick);
    if (typeof BroadcastChannel !== 'undefined') {
      this.#cartBroadcast = new BroadcastChannel('b2b-plus-cart');
      this.#cartBroadcast.addEventListener('message', this.#onCrossTabCartMessage);
    }
  }

  #unbindCartBridgeListeners() {
    if (!this.#cartBridgeListenersBound) return;
    this.#cartBridgeListenersBound = false;
    const sr = this.#shadowRoot;
    sr.removeEventListener('b2b:cart-filter', this.#onShadowCartFilter);
    sr.removeEventListener('b2b:cart-bulk-apply', this.#onShadowCartBulkApply);
    sr.removeEventListener('b2b:multi-apply-date', this.#onShadowMultiApplyDate);
    sr.removeEventListener('b2b:shared-shipment-date-changed', this.#onSharedShipmentDateChanged);
    sr.removeEventListener('b2b:cart-saved', this.#onShadowCartSaved);
    sr.removeEventListener('b2b:table-error-shown', this.#onTableErrorShown);
    sr.removeEventListener('b2b:cart-issues-resolved', this.#onIssuesResolved);
    this.#issuesBarEl?.removeEventListener('click', this.#onIssuesBarClick);
    this.#issuesBarEl?.removeEventListener('change', this.#onIssuesBarChange);
    this.#unbindIssuesBarOffset();

    this.removeEventListener('b2b:quick-add-added', this.#onQuickAddAdded);
    this.removeEventListener('b2b:product-removed', this.#onProductRemovedFromHost);
    this.#clearCartBtn?.removeEventListener('click', this.#onClearCartBtnClick);
    this.#clearCartBtn = null;
    this.#cartBroadcast?.close();
    this.#cartBroadcast = null;
  }

  #onPageShow = (e) => {
    if (!e.persisted) return;
    void this.#recoverFromBfcache();
  };

  #bindPageShowRecovery() {
    if (this.#pageShowBound) return;
    this.#pageShowBound = true;
    window.addEventListener('pageshow', this.#onPageShow);
  }

  #unbindPageShowRecovery() {
    if (!this.#pageShowBound) return;
    this.#pageShowBound = false;
    window.removeEventListener('pageshow', this.#onPageShow);
    if (this.#bfcacheReloadTimer) {
      clearTimeout(this.#bfcacheReloadTimer);
      this.#bfcacheReloadTimer = null;
    }
  }

  /**
   * Browser Back from checkout often restores this page from bfcache: loaders still
   * visible, IndexedDB connection already closed, and the Liquid `migrateCart`
   * bootstrap does not re-run. Reopen storage, drop frozen UI, and reload the draft
   * (`#loadCartItems` pulls an abandoned Shopify cart back in first). A watchdog
   * full-reload covers a hung IDB transaction.
   */
  async #recoverFromBfcache() {
    resetCartStorageConnection();
    this.#hidePageLoader();
    document.querySelectorAll(
      '[data-page-loader], [data-order-processing], [data-working-order-loading], .b2b-plus-checkout-modal',
    ).forEach((el) => el.remove());
    this.#pageLoaderEl = null;

    this.#loadQueued = false;
    this.#loadInFlight = null;
    this.#cartIsLoading = false;

    if (this.#bfcacheReloadTimer) clearTimeout(this.#bfcacheReloadTimer);
    this.#bfcacheReloadTimer = setTimeout(() => {
      this.#bfcacheReloadTimer = null;
      window.location.reload();
    }, 8000);

    try {
      await this.#loadCartItems();
    } finally {
      if (this.#bfcacheReloadTimer) {
        clearTimeout(this.#bfcacheReloadTimer);
        this.#bfcacheReloadTimer = null;
      }
      this.#hidePageLoader();
    }
  }

  /**
   * Pull an abandoned native checkout back into IndexedDB.
   *
   * `checkout()` copies the draft onto the Shopify cart and clears IndexedDB, but
   * leaves the working order on the server. Page-load sync usually puts that record
   * back before this runs. This still imports the Shopify cart when the draft is
   * empty (sync failed), on a normal cart load and on a bfcache restore.
   * @returns {Promise<void>}
   */
  async #restoreAbandonedShopifyCart() {
    if (!this.#restoreAbandonedPromise) {
      this.#restoreAbandonedPromise = this.#importAbandonedShopifyCart()
        .finally(() => { this.#restoreAbandonedPromise = null; });
    }
    return this.#restoreAbandonedPromise;
  }

  /** @returns {Promise<void>} */
  async #importAbandonedShopifyCart() {
    const cartUrl = window.b2bPlus?.config?.API?.cartUrl ?? '/cart';
    let shopifyCart = null;
    try {
      const res = await fetch(`${cartUrl}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!res.ok) return;
      shopifyCart = await res.json();
    } catch (err) {
      console.warn('b2b-plus: could not read the Shopify cart while restoring an abandoned checkout', err);
      return;
    }
    if (!Array.isArray(shopifyCart?.items) || shopifyCart.items.length === 0) return;

    const didMigrate = await migrateCart(shopifyCart);
    if (!didMigrate) return;

    // Only reached when the draft was still empty, so the Shopify cart is the copy
    // that has to be pushed. A draft the server already restored is left alone.
    try {
      await window.b2bPlus?.workingOrder?.flush?.();
    } catch (err) {
      console.warn('b2b-plus: working-order flush after restoring an abandoned checkout failed', err);
    }
  }

  connectedCallback() {
    const shadowHasShell = Boolean(this.#shadowRoot.querySelector('[data-cart-items-list]'));

    if (!shadowHasShell) {
      const template = this.querySelector('template');
      if (!template && !this.dataset.b2bTemplateRetried) {
        // connectedCallback can fire before the parser has processed this
        // element's children when the custom element is already defined at
        // parse time (upgrade race).  A microtask is NOT sufficient here
        // because it runs between HTML-parser macro-tasks and can still
        // fire before the <template> child's parsing chunk is processed.
        // setTimeout(0) enqueues a new macro-task that the browser schedules
        // *after* already-queued parser tasks, guaranteeing the full
        // subtree is available when we retry.
        this.dataset.b2bTemplateRetried = '1';
        setTimeout(() => { if (this.isConnected) this.connectedCallback(); }, 0);
        return;
      }
      if (!template) {
        const msg = window.b2bPlus?.strings?.cartTemplateError
          ?? 'B2B cart failed to load — please contact support or refresh the page.';
        const errEl = document.createElement('p');
        errEl.setAttribute('role', 'alert');
        errEl.style.cssText = 'padding:2rem;color:#922;font-family:sans-serif;font-size:1rem;';
        errEl.textContent = msg;
        this.#shadowRoot.appendChild(errEl);
        console.error('b2b-plus: <b2b-plus-cart> is missing its <template> child. ' +
          'Verify that b2b-plus-cart.liquid is rendering correctly.');
        return;
      }
      this.#shadowRoot.appendChild(template.content.cloneNode(true));
    }

    this.#cartItemsContainer = this.#shadowRoot.querySelector('[data-cart-items-list]');
    this.#filterEmptyState   = this.#shadowRoot.querySelector('[data-filter-empty-state]');
    this.#cartErrorsEl       = this.#shadowRoot.querySelector('[data-cart-errors]');
    this.#issuesBarEl        = this.#ensureIssuesBarEl();
    this.#cartHeaderEl       = this.#shadowRoot.querySelector('b2b-plus-cart-header');
    this.#cartFooterEl       = this.#shadowRoot.querySelector('b2b-plus-cart-footer');
    this.#cartFooterWrapEl   = this.#shadowRoot.querySelector('[data-cart-footer]')
                               ?? this.#cartFooterEl;

    this.#poBoxEl = this.#shadowRoot.querySelector('[data-po-box-field]');
    if (this.#poBoxEl) {
      this.#poBoxEl.hidden = true;
      if (!isPoNumberEnabled()) this.#poBoxEl.innerHTML = '';
    }
    this.#clearCartActionEl = this.#shadowRoot.querySelector('[data-clear-cart-action]');
    this.#updateClearCartAction(false);

    this.#layoutToggleRoot = this.#shadowRoot.querySelector('[data-cart-layout-toggle]');
    this.#initCartLineLayoutToggle();

    this.#bindCartBridgeListeners();
    this.#bindPageShowRecovery();

    // Always run the SR-switch check before loading cart data.
    // Calling it here — inside the component — guarantees it runs on every
    // cart-page load regardless of whether the layout-level integration
    // remembered to invoke it.  #loadCartItems() is intentionally deferred
    // until the check settles so we never render a previous SR's items to a
    // newly-selected customer.
    manageCartSwitch()
      .then(({ switched, cleared }) => {
        if (switched && cleared) {
          console.info('b2b-plus: SR customer changed — local cart cleared before loading.');
        } else if (switched) {
          console.info('b2b-plus: SR customer changed — kept the just-migrated saved order.');
        }
      })
      .catch((err) => {
        // A switch-check failure must not block the cart from rendering.
        // Log loudly so developers notice, but continue loading.
        console.error('b2b-plus: manageCartSwitch() failed — cart loaded without switch check.', err);
      })
      // Complete any deferred saved-order migrate (edit-order → switch → reload)
      // BEFORE loading items, so the cart renders the migrated order in one pass
      // rather than showing empty and needing a refresh. Memoized in utils, so this
      // shares the single run kicked off by the global bootstrap.
      .finally(() =>
        runPendingSavedOrderMigration().catch((err) => {
          console.error('b2b-plus: pending saved-order migrate failed', err);
        }),
      )
      .finally(() => {
        this.#loadCartItems();
      });
  }

  disconnectedCallback() {
    // Clear the upgrade-race retry flag so a reconnect (e.g. Shopify
    // live-reload) gets a clean retry rather than hitting the error branch.
    delete this.dataset.b2bTemplateRetried;
    this.#unbindCartBridgeListeners();
    this.#unbindPageShowRecovery();
    this.#layoutToggleRoot?.removeEventListener('click', this.#onCartLayoutToggleClick);
    this.#cartItemsContainer?.removeEventListener('click', this.#onCartEditQtyClick);
    this.#cartItemsContainer?.removeEventListener('click', this.#onCartGridRemoveClick);
    clearTimeout(this.#cartSavedDebounceTimer);
    this.#cartSavedDebounceTimer = null;
    clearTimeout(this.#crossTabCartReloadTimer);
    this.#crossTabCartReloadTimer = null;
    this.#clearUpdatingDataClass();
    this.#sentinelObserver?.disconnect();
    this.#sentinelObserver = null;
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  /**
   * Public entry point for (re)loading the cart. Serializes overlapping calls —
   * connectedCallback, cross-tab BroadcastChannel, clear-cart retry, quick-add
   * fallback, and checkout-popup close can all trigger a load. Without a guard
   * these interleave and corrupt pagination / footer state. A single re-run is
   * queued if a load is requested mid-flight so the final state stays fresh.
   * @returns {Promise<void>}
   */
  async #loadCartItems() {
    if (this.#loadInFlight) {
      this.#loadQueued = true;
      return this.#loadInFlight;
    }
    this.#loadInFlight = this.#runLoadCartItems();
    try {
      await this.#loadInFlight;
    } finally {
      this.#loadInFlight = null;
      if (this.#loadQueued) {
        this.#loadQueued = false;
        return this.#loadCartItems();
      }
    }
  }

  async #runLoadCartItems() {
    this.#cartIsLoading = true;
    this.#syncClearCartLoadingState();
    try {
      await this.#loadCartItemsInner();
    } catch (err) {
      console.error('b2b-plus: #loadCartItems failed', err);
      this.#renderCartItemsMessage(cartLoadErrorTemplate({ onRetry: () => this.#retryCartLoad() }));
      this.#syncCartBulkApplyOnTables();
    } finally {
      this.#cartIsLoading = false;
      this.#syncClearCartLoadingState();
    }
  }

  /**
   * "Retry" action on the cart load-error banner.
   *
   * A plain reload is not enough: the usual cause of a failed load is a stale B2B
   * company-location session, which makes Shopify reject the cart write behind
   * `getProductJSON()` with a 422 on every subsequent attempt too. So we re-apply the
   * ALREADY-ACTIVE location through Shopify's native `/company_location/update`
   * endpoint — the same thing the pick-customer modal navigates to after a switch —
   * which re-resolves the location server-side and returns to this page.
   *
   * Falls back to a plain reload when no location id is resolvable (e.g. a non-B2B
   * customer, where a transient fetch failure is the likelier cause anyway).
   * @returns {Promise<void>}
   */
  async #retryCartLoad() {
    const btn = this.#cartItemsContainer?.querySelector('[data-cart-load-retry]');
    if (btn) btn.disabled = true;

    try {
      // b2b-plus/utils, not b2b-plus/checkout-sdk: `resolveActiveLocationGid` is all we
      // want here, and utils is already loaded, so this resolves from cache instead of
      // dragging the ~120 KB checkout SDK in behind a retry click.
      const [{ resolveActiveLocationGid }, { buildLocationSwitchUrl }] = await Promise.all([
        import('b2b-plus/utils'),
        import('b2b-plus/pick-customer-sdk'),
      ]);
      const url = buildLocationSwitchUrl(resolveActiveLocationGid(), window.location);
      if (url) {
        window.location.assign(url);
        return;
      }
    } catch (err) {
      console.error('b2b-plus: cart retry could not re-apply the active location', err);
    }
    window.location.reload();
  }

  /** Sync only the disabled state of the clear-cart button without touching visibility. */
  #syncClearCartLoadingState() {
    const btn = this.#clearCartActionEl?.querySelector('[data-clear-cart-btn]');
    if (btn) btn.disabled = this.#cartIsLoading || this.#isClearingCart;
  }

  /**
   * Render a full-container message (loading / empty / load-error) into the cart
   * items container.
   *
   * Always renders into a FRESH child element. `#loadCartItemsInner` clears the
   * container with `innerHTML = ''` before paginated rendering, which detaches
   * lit-html's anchor nodes while leaving the `_$litPart$` property behind on the
   * container. A later `render()` of a DIFFERENT template into that same container
   * makes lit insert against a now-parentless anchor and throw — which surfaced as
   * the cart load-error banner the first time a reload found the cart empty
   * (e.g. after deleting the last shipment in the checkout modal). A fresh child
   * carries its own part and is disposed of by the same `innerHTML = ''`.
   *
   * @param {import('lit-html').TemplateResult} template
   */
  #renderCartItemsMessage(template) {
    const container = this.#cartItemsContainer;
    if (!container) return;
    container.innerHTML = '';
    const root = document.createElement('div');
    container.appendChild(root);
    render(template, root);
  }

  /**
   * Reset the chrome that only makes sense with items in the cart (footer totals,
   * date tabs / filters). Mirrors what `#removeProductTable` does when it removes
   * the last table, for the load path that discovers an empty cart.
   */
  #resetChromeForEmptyCart() {
    clearTimeout(this.#cartSavedDebounceTimer);
    this.#cartSavedDebounceTimer = null;
    this.#cartFooterEl?.init({ totalUnits: 0, totalProducts: 0, subtotal: 0, cartProductTableCount: 0 });
    this.#cartHeaderEl?.init(); // clears date tabs and filter dropdowns
    this.#activeFilters = null;
    this.#filterTableCache = [];
    this.#filterRowCache.clear();
    if (this.#filterEmptyState) this.#filterEmptyState.hidden = true;
  }

  async #loadCartItemsInner() {
    const { strings } = window.b2bPlus;
    const itemsContainer = this.#cartItemsContainer;

    // Reset pagination state for a fresh load.
    this.#sentinelObserver?.disconnect();
    this.#sentinelObserver = null;
    this.#loadedOffset     = 0;
    this.#validProducts    = [];
    this.#cartData         = null;
    // Every banner and issue describes THIS load.
    this.#banners.clear();
    this.#renderBanners();
    this.#clearCartIssues();

    this.#renderCartItemsMessage(cartLoadingTemplate());

    // Native checkout empties IndexedDB and leaves the order on the Shopify cart.
    // Back from /checkout restores this document from bfcache, or from HTML rendered
    // before that hand-off (when `cart.items` was still empty), so the Liquid
    // migrate bootstrap never sees the lines. Import them before rendering.
    await this.#restoreAbandonedShopifyCart();

    // Guard against a race where this cart's connectedCallback fires before the
    // <b2b-plus-product-table> module has finished evaluating (and called
    // customElements.define). In production with the static module order in
    // b2b-plus-config.liquid this is a no-op, but Shopify dev HMR / a slow
    // ESM CDN can briefly leave the element unregistered, in which case
    // document.createElement('b2b-plus-product-table') below would return a
    // plain HTMLElement and tableEl.setPrefetchedData() would throw.
    // whenDefined() resolves immediately when already registered.
    if (!customElements.get('b2b-plus-product-table')) {
      const definedPromise = customElements.whenDefined('b2b-plus-product-table');
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error(
            'b2b-plus-product-table custom element was not registered within 10s — '
            + 'check that b2b-plus-product-table.js loaded successfully (Network tab) '
            + 'and that no earlier module errored before customElements.define() ran.',
          )),
          10_000,
        ),
      );
      await Promise.race([definedPromise, timeoutPromise]);
    }

    // 1. Load all storage keys from IndexedDB.
    //    Keys are either plain handles ("my-product") or option-scoped ("my-product::Red").
    const cartData    = await getCart();
    const storageKeys = Object.keys(cartData ?? {});

    this.#cartData = cartData;
    this.#updateClearCartAction(storageKeys.length > 0);

    if (!storageKeys.length) {
      this.#renderCartItemsMessage(cartEmptyItemsTemplate());
      this.#resetChromeForEmptyCart();
      this.#updatePoBox('all');
      this.#syncCartBulkApplyOnTables();
      return;
    }

    // 2. Parse storage keys to extract unique base handles for the product JSON fetch.
    //    "my-product::Red" and "my-product::Blue" both resolve to base handle "my-product".
    const baseHandleMap = new Map(); // baseHandle → storageKey[]
    for (const key of storageKeys) {
      const { baseHandle } = parseStorageKey(key);
      if (!baseHandleMap.has(baseHandle)) baseHandleMap.set(baseHandle, []);
      baseHandleMap.get(baseHandle).push(key);
    }

    // The catalog-price API resolves products by id, so each handle rides the ids
    // stored on its records (product id when present, variant ids for older carts).
    const lookups         = await getCartProductLookups();
    const fetchedProducts = await getProductJSON(lookups);
    const fetchedList = (Array.isArray(fetchedProducts) ? fetchedProducts : [fetchedProducts])
      .filter((p) => p?.handle);
    await Promise.all(fetchedList.map((p) => enrichProductJSONWithSeasonPrices(p)));
    const fetchedByHandle = new Map(fetchedList.map((p) => [p.handle, p]));

    // Remove IDB entries whose base product no longer exists in Shopify.
    const unavailableKeys = storageKeys.filter(
      (k) => !fetchedByHandle.has(parseStorageKey(k).baseHandle),
    );
    if (unavailableKeys.length) {
      // Guard against destructive clears on a transient fetch failure. If the
      // section render returned NOTHING for a non-empty cart, that is almost
      // certainly a network/Shopify error — not "every product was deleted".
      // Aborting (and showing a retryable load error) preserves the buyer's
      // order instead of wiping IndexedDB. Only prune individually-missing
      // handles when at least one product came back successfully.
      if (fetchedByHandle.size === 0) {
        throw new Error(
          'b2b-plus: product-data fetch returned no products for a non-empty cart — '
          + 'aborting load to avoid clearing IndexedDB on a transient failure.',
        );
      }
      await Promise.all(unavailableKeys.map((k) => clearCart(k)));
      const unavailableBases = [...new Set(unavailableKeys.map((k) => parseStorageKey(k).baseHandle))];
      this.#showUnavailableError(unavailableBases, cartData, strings);
    }

    // 3. Build validProducts — expand split-option products into per-value virtual entries.
    //    Each virtual entry has its storage key as `handle` so all downstream cart ops
    //    (read, write, remove) target the correct IDB key automatically.
    const splitOption   = String(window.b2bPlus?.config?.productTableSplitOption ?? '').trim();
    const validProducts = expandValidProducts(baseHandleMap, fetchedByHandle, splitOption, {
      optionLabel,
      // The storage key's colour label is the storefront translation. Catalog
      // product JSON is not, so a French key (`Rouge à joues`) needs the
      // variant ids on the cart record to find its colour group.
      variantIdsForKey: (storageKey) => variantIdsFromCartItems(cartData?.[storageKey]),
    });

    if (!validProducts.length) {
      this.#renderCartItemsMessage(cartEmptyItemsTemplate());
      this.#resetChromeForEmptyCart();
      this.#updatePoBox('all');
      this.#updateClearCartAction(false);
      this.#syncCartBulkApplyOnTables();
      return;
    }

    // 4. Fetch inventory for all products in one batched API call.
    const productIds = [...new Set(validProducts.map((product) => product.id).filter(Boolean))];
    // const inventoryResponse = await getInventory(productIds, {});

    const lockedType = cartLockConfig.enabled ? deriveLockedType(cartData) : null;
    const inventoryResponse = await getInventory(productIds, lockedType ? { type: lockedType } : {});

    const inventoryData     = inventoryResponse?.success && inventoryResponse?.data
      ? inventoryResponse.data : null;

    this.#inventoryData  = inventoryData;
    this.#validProducts  = validProducts;

    // 4. Collect delivery windows and populate the cart header filter bar
    //    with options derived from the full inventory dataset.
    const selectedDeliveryWindows = await this.#collectDeliveryWindows(cartData);

    const inventoryFilterOptions = getInventoryFilterData(inventoryData);
    this.#inventoryFilterOptions = inventoryFilterOptions;

    const { config } = window.b2bPlus;
    const inventoryDateRange = buildInventoryDateRange(
      getInventoryDateRange(inventoryData),
      { enableMultiApply: config?.enableMultiApply },
      { toISODate },
    );

    const shipmentTotals = computeShipmentTotals(validProducts, cartData, displayedUnitCents);

    this.#cartHeaderEl?.init({
      dates:             selectedDeliveryWindows,
      seasons:           inventoryFilterOptions.seasons,
      types:             inventoryFilterOptions.types,
      inventoryDateRange,
      shipmentTotals,
    });

    // 5. Compute grand totals from the full cart (all products, not just the
    //    first page). Footer is updated once here so the user always sees the
    //    correct numbers regardless of how far they have scrolled.
    this.#cartFooterEl?.init({
      ...computeCartTotals(validProducts, cartData, displayedUnitCents),
      shipmentTotals,
    });
    this.#cartFooterEl?.enable({
      beforeCheckout:   () => this.#validateCheckout(),
      onCheckoutError:  () => this.#setCartLocked(false),
      onCheckoutReady:  () => this.#proceedToCheckout(),
    });

    // 6. Clear loading placeholder and start paginated DOM rendering.
    //    Reset filter caches so stale element references from a previous load
    //    are not carried over into the new render.
    this.#filterTableCache = [];
    this.#filterRowCache.clear();
    if (itemsContainer) itemsContainer.innerHTML = '';
    // Show what is already wrong with the saved order as soon as the cart opens —
    // scanned from the snapshot read above, before the tables clamp it.
    this.#issueSources = {
      dom:       [],
      records:   [],
      inventory: [],
      load:      this.#scanLoadIssues(validProducts, cartData, inventoryData),
    };
    this.#publishIssues();
    this.#syncCartLineLayoutAttr();
    this.#renderNextBatch();
    this.#syncCartBulkApplyOnTables();
    this.#updatePoBox(this.#activeFilters?.date ?? 'all');
  }

  #updateClearCartAction(hasCart) {
    // Single choke point for every "cart has items" affordance. The totals / subtotal /
    // checkout footer is one of them: with an empty cart it would only show zeroes, so
    // hide the whole block. Done before the clear-cart guard below so a theme without
    // the clear-cart button still gets the footer toggle.
    if (this.#cartFooterWrapEl) this.#cartFooterWrapEl.hidden = !hasCart;

    const action = this.#clearCartActionEl;
    if (!action) return;
    action.hidden = !hasCart;
    const btn = action.querySelector('[data-clear-cart-btn]');
    if (btn) btn.disabled = this.#isClearingCart || this.#cartIsLoading;
    // Layout toggle tracks the same cart-has-items state — hidden on empty cart.
    if (this.#layoutToggleRoot) {
      this.#layoutToggleRoot.hidden = !this.#isCartLineLayoutToggleEnabled() || !hasCart;
    }
  }

  /**
   * When false, cart behaves as before: no layout toggle and tables only.
   * @returns {boolean}
   */
  #isCartLineLayoutToggleEnabled() {
    return window.b2bPlus?.config?.cart?.enableCartLineLayoutToggle !== false;
  }

  #syncCartLineLayoutAttr() {
    const wrap = this.#cartItemsContainer;
    if (!wrap) return;
    if (!this.#isCartLineLayoutToggleEnabled()) {
      wrap.removeAttribute('data-cart-line-layout');
      return;
    }
    wrap.setAttribute('data-cart-line-layout', this.#cartLineLayout);
  }

  #initCartLineLayoutToggle() {
    this.#layoutToggleRoot?.removeEventListener('click', this.#onCartLayoutToggleClick);
    this.#cartItemsContainer?.removeEventListener('click', this.#onCartEditQtyClick);
    this.#cartItemsContainer?.removeEventListener('click', this.#onCartGridRemoveClick);
    if (!this.#isCartLineLayoutToggleEnabled()) {
      if (this.#layoutToggleRoot) this.#layoutToggleRoot.hidden = true;
      this.#cartItemsContainer?.removeAttribute('data-cart-line-layout');
      return;
    }
    // Visibility is owned by cart state (#updateClearCartAction): the toggle
    // stays hidden until the cart has items, so an empty cart shows no toggle.
    this.#layoutToggleRoot?.addEventListener('click', this.#onCartLayoutToggleClick);
    this.#cartItemsContainer?.addEventListener('click', this.#onCartEditQtyClick);
    this.#cartItemsContainer?.addEventListener('click', this.#onCartGridRemoveClick);
    this.#setCartLineLayout(this.#cartLineLayout);
  }

  /**
   * @param {'list'|'grid'} layout
   */
  #setCartLineLayout(layout) {
    if (!this.#isCartLineLayoutToggleEnabled()) return;
    this.#cartLineLayout = layout === 'grid' ? 'grid' : 'list';
    this.#syncCartLineLayoutAttr();
    const root = this.#layoutToggleRoot;
    if (root) {
      for (const b of root.querySelectorAll('[data-cart-layout]')) {
        const on = b.dataset.cartLayout === this.#cartLineLayout;
        b.classList.toggle('active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    }
  }

  /**
   * Wraps each cart product table in a slot when the layout toggle is enabled (edit-quantity control).
   * @param {Element} container
   * @param {HTMLElement} tableEl
   * @param {{ prepend?: boolean }} [opts]
   */
  #appendCartProductLine(container, tableEl, { prepend = false } = {}) {
    if (!container || !tableEl) return;
    if (!this.#isCartLineLayoutToggleEnabled()) {
      if (prepend) container.prepend(tableEl);
      else container.appendChild(tableEl);
      return;
    }
    const slot = document.createElement('div');
    slot.className = 'b2b-plus-cart-line-slot';
    slot.setAttribute('data-b2b-plus-cart-line-slot', '');

    render(this.#cartLineSlotActionsTemplate(tableEl), slot);

    if (prepend) container.prepend(slot);
    else container.appendChild(slot);
  }

  async #onClearCartClicked() {
    if (this.#isClearingCart) return;

    const confirmMessage =
      b2bPlusString('cartClearConfirm');
    if (!window.confirm(confirmMessage)) return;

    this.#isClearingCart = true;
    this.#updateClearCartAction(true);
    let didClear = false;
    try {
      await clearCart();
      didClear = true;
      this.#renderCartFullyClearedState();
      toastNotification(
        b2bPlusString('cartClearSuccess'),
        'success',
      );
    } catch (err) {
      console.error('b2b-plus: clear cart action failed', err);
      toastNotification(
        b2bPlusString('cartClearError'),
        'error',
      );
    } finally {
      this.#isClearingCart = false;
      this.#updateClearCartAction(!didClear);
      if (!didClear) this.#loadCartItems();
    }
  }

  #renderCartFullyClearedState() {
    const container = this.#cartItemsContainer;
    if (container) {
      container.innerHTML = '';
      const empty = document.createElement('div');
      empty.className = 'b2b-plus-empty-state';
      empty.setAttribute('data-b2b-plus-empty-state', '');
      empty.textContent = b2bPlusString('cartEmpty');
      container.appendChild(empty);
      this.#syncCartLineLayoutAttr();
    }

    clearTimeout(this.#cartSavedDebounceTimer);
    this.#cartSavedDebounceTimer = null;
    this.#clearUpdatingDataClass();
    this.#sentinelObserver?.disconnect();
    this.#sentinelObserver = null;

    this.#cartData = {};
    this.#validProducts = [];
    this.#inventoryData = null;
    this.#inventoryFilterOptions = null;
    this.#loadedOffset = 0;
    this.#activeFilters = null;
    this.#filterTableCache = [];
    this.#filterRowCache.clear();

    this.#cartFooterEl?.init({ totalUnits: 0, totalProducts: 0, subtotal: 0, cartProductTableCount: 0 });
    this.#cartHeaderEl?.init();
    this.#updatePoBox('all');
    if (this.#filterEmptyState) this.#filterEmptyState.hidden = true;
    this.#syncCartBulkApplyOnTables();
  }

  // ── Paginated rendering ───────────────────────────────────────────────────

  /**
   * Render the next page of product tables into the cart items container.
   * Attaches an IntersectionObserver sentinel after the last rendered row
   * so the following batch is triggered automatically when the user scrolls
   * close to the bottom.
   */
  #renderNextBatch() {
    const { config } = window.b2bPlus;
    const { cartItemsLimit } = config.cart;
    const itemsContainer = this.#cartItemsContainer;
    if (!itemsContainer) return;

    // Remove any existing sentinel before appending the new batch.
    itemsContainer.querySelector('[data-load-more-sentinel]')?.remove();

    const batch = this.#validProducts.slice(this.#loadedOffset, this.#loadedOffset + cartItemsLimit);
    if (!batch.length) return;

    for (const productJSON of batch) {
      const existingCartItems = Array.isArray(this.#cartData?.[productJSON.handle])
        ? this.#cartData[productJSON.handle] : [];
      const tableEl = this.#createProductTableElement(productJSON, this.#inventoryData, existingCartItems);
      this.#appendCartProductLine(itemsContainer, tableEl);
      this.#filterTableCache.push(tableEl);
      const issues = this.#issuesForHandle(productJSON.handle);
      if (issues.length) tableEl.setCartIssues?.(issues);
    }

    this.#loadedOffset += batch.length;

    if (this.#filterEmptyState) this.#filterEmptyState.hidden = true;

    // Re-apply active filters to newly rendered tables so they honour
    // whatever the user has already selected in the filter bar.
    if (this.#activeFilters) {
      this.#applyFilters(this.#activeFilters);
    }

    // If more products remain, place a 1px sentinel below the last table and
    // observe it. When it enters the viewport (plus a 300 px root margin for
    // early triggering), disconnect and render the next batch.
    if (this.#loadedOffset < this.#validProducts.length) {
      const sentinel = document.createElement('div');
      sentinel.setAttribute('data-load-more-sentinel', '');
      sentinel.style.cssText = 'height:1px;width:100%;pointer-events:none;';
      itemsContainer.appendChild(sentinel);

      this.#sentinelObserver?.disconnect();
      this.#sentinelObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.#sentinelObserver?.disconnect();
            this.#sentinelObserver = null;
            this.#renderNextBatch();
          }
        },
        { rootMargin: '300px' },
      );
      this.#sentinelObserver.observe(sentinel);
    }

    this.#syncCartBulkApplyOnTables();
  }

  /**
   * Synchronously render every remaining product-table batch (bypassing the
   * scroll sentinel) and await each table's first render, so an operation that
   * must touch all line items — e.g. cart-wide bulk apply — sees the full set
   * rather than only the scrolled-into-view subset. No-op when all batches are
   * already rendered.
   * @returns {Promise<void>}
   */
  async #renderAllRemainingTables() {
    // #renderNextBatch advances #loadedOffset and removes/re-adds the sentinel,
    // so looping it drains every batch into #filterTableCache + the DOM.
    while (this.#loadedOffset < this.#validProducts.length) {
      const before = this.#loadedOffset;
      this.#renderNextBatch();
      if (this.#loadedOffset <= before) break; // guard against a stalled batch
    }
    // connectedCallback finishes asynchronously; wait for rows to exist.
    await Promise.all(
      this.#filterTableCache.map((t) => (typeof t.whenReady === 'function' ? t.whenReady() : Promise.resolve())),
    );
  }

  // ── Cart state helpers ────────────────────────────────────────────────────

  /**
   * Products removed this load because Shopify no longer returns them. Their JSON never
   * resolved, so the handle and each saved line's inventory window are all there is to
   * name them by — one entry per (handle, window).
   * @param {string[]} handles     - Base handles.
   * @param {object|null} cartData - `getCart()` snapshot (storage key → line items).
   * @param {object} strings
   */
  #showUnavailableError(handles, cartData, strings) {
    const wanted = new Set(handles);
    const lines = [];
    for (const [key, items] of Object.entries(cartData ?? {})) {
      const { baseHandle } = parseStorageKey(key);
      if (!wanted.has(baseHandle)) continue;
      for (const item of Array.isArray(items) ? items : []) {
        if (Number(item?.quantity) <= 0) continue;
        lines.push(describeCartLine({ properties: item?.properties, handle: baseHandle }));
      }
    }
    for (const handle of wanted) {
      if (!lines.some((d) => d.handle === handle)) lines.push(describeCartLine({ handle }));
    }
    const msg = strings.cartUnavailableRemoved
      || '{count} item(s) are no longer available and have been removed from your cart:\n{handles}';
    this.#setBanner(
      'unavailable-removed',
      cartMessageWithLinesTemplate(msg, uniqueLineDescriptors(lines), { count: handles.length }),
    );
  }

  /**
   * Await any in-flight debounced auto-save on every rendered product table so a
   * subsequent IDB read (record completeness + fresh inventory) sees the buyer's
   * latest quantities/dates rather than a pre-debounce snapshot.
   * @returns {Promise<void>}
   */
  async #flushPendingTableSaves() {
    const container = this.#cartItemsContainer;
    if (!container) return;
    const tables = [...container.querySelectorAll('b2b-plus-product-table')];
    await Promise.all(tables.map((t) => {
      try { return t.flushPendingSave?.() ?? Promise.resolve(); }
      catch { return Promise.resolve(); }
    }));
  }

  async #validateCheckout() {
    // 0. Flush any pending debounced table saves FIRST. Qty edits auto-save on a
    //    1s debounce; without this, a buyer who types a quantity/date and clicks
    //    checkout within that window would be validated against the stale record —
    //    producing a false "needs a delivery date and at least one quantity" error.
    await this.#flushPendingTableSaves();
    // A new attempt: the previous attempt's banners and issues no longer describe the cart.
    this.#clearCheckoutBanners();
    this.#clearCartIssues();
    // 1. DOM validation of rendered tables — inline errors + issues for the strip.
    if (!this.#validateCheckoutDom()) {
      // Add the unrendered products' past dates and run the fresh-inventory check too,
      // so the strip lists every problem at once rather than one category per attempt.
      // A pass leaves the cart locked for the checkout hand-off (see #validateInventory)
      // — release it, nothing proceeds.
      this.#setIssueSource('records', await this.#pastDatedRecordIssues({ skipRendered: true }));
      if (await this.#validateInventory()) {
        this.#setCartLocked(false);
        this.#cartFooterEl?.setCheckoutProgress?.('idle');
        this.#cartFooterEl?.syncCheckoutCtaFromTotals?.();
      }
      this.#revealFirstIssue();
      return false;
    }
    // 2. Render-independent gate over EVERY product in the cart (including
    //    paginated pages that were never rendered and tables whose rows are
    //    still streaming) so invalid lines on page 2+ cannot bypass checkout.
    if (!(await this.#validateAllCartRecords())) return false;
    // 3. Cart-level minimum order quantity (config: cart.minimunQuantity).
    if (!(await this.#validateMinimumQuantity())) return false;
    // 3b. Cart-level minimum order value (config: cart.minimumOrderValue).
    if (!(await this.#validateMinimumOrderValue())) return false;
    // 4. Fresh inventory / stock validation.
    if (!(await this.#validateInventory())) {
      this.#revealFirstIssue();
      return false;
    }
    return true;
  }

  /**
   * Enforce the per-shipment minimum order value when
   * `config.cart.minimumOrderValue.enable` is set and a positive `threshold`
   * (cents) is given. Uses the same shipment totals as the footer / date tabs
   * so the gate matches the live alert / disabled CTA state.
   * @returns {Promise<boolean>}
   */
  async #validateMinimumOrderValue() {
    const minVal = window.b2bPlus.config?.cart?.minimumOrderValue;
    const threshold = Number(minVal?.threshold) || 0;
    if (!minVal?.enable || threshold <= 0) return true;

    const validProducts = this.#validProducts ?? [];
    if (!validProducts.length) return true;

    let cartData;
    try {
      cartData = await getCart();
    } catch (err) {
      console.warn('b2b-plus: validateMinimumOrderValue read failed', err);
      return true;
    }
    if (!cartData) return true;

    const shipmentTotals = computeShipmentTotals(validProducts, cartData, displayedUnitCents);
    if (meetsMinimumOrderValuePerShipment(shipmentTotals, minVal)) return true;

    // Footer CTA + alert already reflect this on every totals update; keep the
    // popup closed and scroll the buyer to the visible message.
    this.#cartFooterEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    return false;
  }

  /**
   * Enforce the cart-level minimum order quantity when
   * `config.cart.minimunQuantity.enable` is set and a positive `threshold` is
   * given. Totals every cart line's units straight from storage (render
   * independent, mirrors the footer total) and blocks checkout with a banner
   * when the grand total falls below the threshold.
   * @returns {Promise<boolean>}
   */
  async #validateMinimumQuantity() {
    const minQty = window.b2bPlus.config.cart?.minimunQuantity;
    const threshold = Number(minQty?.threshold) || 0;
    if (!minQty?.enable || threshold <= 0) return true;

    const validProducts = this.#validProducts ?? [];
    if (!validProducts.length) return true;

    let cartData;
    try {
      cartData = await getCart();
    } catch (err) {
      // A storage read failure here must not false-block checkout — the other
      // validation passes still guard the order.
      console.warn('b2b-plus: validateMinimumQuantity read failed', err);
      return true;
    }
    if (!cartData) return true;

    let totalUnits = 0;
    for (const productJSON of validProducts) {
      const cartItems = Array.isArray(cartData[productJSON.handle])
        ? cartData[productJSON.handle] : [];
      if (!cartItems.length) continue;
      totalUnits += sumCartItems(productJSON, cartItems).units;
    }

    if (totalUnits >= threshold) {
      this.#cartFooterEl?.clearCheckoutError?.();
      return true;
    }

    // Surface the error inside the footer, directly below the checkout button,
    // rather than in the top-of-cart banner.
    const { strings } = window.b2bPlus;
    const template = strings.cartCheckoutMinOrderQuantity
      || 'Order minimum of {threshold} units required.';
    const msg = template.replace('{threshold}', threshold.toLocaleString());
    this.#cartFooterEl?.showCheckoutError?.(msg);
    this.#cartFooterEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    return false;
  }

  /**
   * Validate the IndexedDB cart records for every product (not just rendered
   * tables). Each product must have at least one quantity > 0, and every row
   * with quantity must carry a delivery window — mirroring the per-table
   * `validate()` checks but reading straight from storage so unrendered /
   * paginated products are covered. Shows a banner listing the offending
   * products and returns false when any fail.
   * @returns {Promise<boolean>}
   */
  async #validateAllCartRecords() {
    const validProducts = this.#validProducts ?? [];
    if (!validProducts.length) return true;

    const problems = [];
    for (const product of validProducts) {
      let record;
      try {
        record = await getProductRecord(product.handle);
      } catch (err) {
        // IDB read failure here should not false-block checkout — the DOM pass
        // and the fresh-inventory check still guard the rendered set.
        console.warn('b2b-plus: validateAllCartRecords read failed', product.handle, err);
        continue;
      }

      if (isProductRecordIncomplete(record)) {
        problems.push(describeCartLine({
          productJSON: product,
          handle:      product._baseHandle ?? product.handle,
        }));
      }
    }

    if (problems.length) {
      const { strings } = window.b2bPlus;
      const msg = strings.cartCheckoutIncompleteItems
        || 'Some products need a delivery date and at least one quantity before checkout:\n{handles}';
      this.#setBanner('checkout-incomplete', cartMessageWithLinesTemplate(msg, uniqueLineDescriptors(problems)));
      this.#cartErrorsEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return false;
    }

    // A restored delivery date that has gone by stays in the cart (flagged, not cleared —
    // see the product table's #flagPastCartDeliveryDates) but can never be ordered. The
    // DOM pass already caught rendered tables, so these are products further down.
    const pastDated = await this.#pastDatedRecordIssues();
    if (pastDated.length) {
      this.#setIssueSource('records', pastDated);
      this.#revealFirstIssue();
      return false;
    }
    return true;
  }

  #validateCheckoutDom() {
    const itemsContainer = this.#cartItemsContainer;
    if (!itemsContainer) return true;

    let hasErrors = false;
    const issues  = [];

    itemsContainer.querySelectorAll('b2b-plus-product-table').forEach((productTable) => {
      const result = productTable.validate();
      if (!result.valid) {
        hasErrors = true;
        issues.push(...(result.issues ?? []));
      }
    });

    // Scrolling to the first issue is the caller's job, once the other passes have added
    // theirs too — #goToIssue flips grid → list and summary → edit before it scrolls.
    this.#setIssueSource('dom', issues);
    return !hasErrors;
  }

  async #validateInventory() {
    if (this.#isValidatingInventory) return false;
    this.#isValidatingInventory = true;

    const checkoutBtn = this.#cartFooterEl?.querySelector('[data-footer-checkout]');

    // Checkout banners were cleared when this attempt began (#validateCheckout);
    // load-scoped ones deliberately stay visible.
    this.#setCartLocked(true);
    this.#cartFooterEl?.setCheckoutProgress?.('validating');
    if (checkoutBtn) checkoutBtn.disabled = true;

    let passed = false;
    try {
      passed = await this.#checkFreshInventory();
    } finally {
      this.#isValidatingInventory = false;
      // Restore only on failure — on success the footer shows validating until
      // `#handleCheckout` switches to proceeding.
      if (!passed) {
        this.#setCartLocked(false);
        this.#cartFooterEl?.setCheckoutProgress?.('idle');
        this.#cartFooterEl?.syncCheckoutCtaFromTotals?.();
      }
    }
    return passed;
  }

  /**
   * Locks or unlocks all interactive areas of the cart (header, action bar, items list)
   * during checkout processing so the buyer cannot edit quantities mid-flight.
   * The footer is intentionally excluded so the checkout CTA / spinner remain visible.
   * @param {boolean} locked
   */
  #setCartLocked(locked) {
    const areas = [
      this.#shadowRoot?.querySelector('.cart-header-wrapper'),
      this.#shadowRoot?.querySelector('.b2b-plus-po-box-wrapper'),
      this.#cartItemsContainer,
    ].filter(Boolean);

    for (const el of areas) {
      el.inert = locked;
      el.style.opacity = locked ? '0.4' : '';
      el.style.pointerEvents = locked ? 'none' : '';
    }
  }

  /**
   * Show a full-screen loader overlay on top of everything (cart + checkout
   * modal, z-index above the modal). Kept in this theme for the bfcache restore
   * path (`#bindPageShow`) — the global checkout launcher owns the loader while a
   * checkout modal is opening, but a Back into a bfcached cart page has to take
   * down whatever overlay the launcher left behind. Idempotent — reuses the
   * single overlay node.
   */
  #showPageLoader() {
    if (!this.#pageLoaderEl) {
      this.#pageLoaderEl = document.createElement('div');
      this.#pageLoaderEl.className = 'b2b-plus-page-loader';
      this.#pageLoaderEl.setAttribute('data-page-loader', '');
      render(cartLoadingTemplate(), this.#pageLoaderEl);
    }
    if (!this.#pageLoaderEl.isConnected) document.body.appendChild(this.#pageLoaderEl);
    this.#pageLoaderEl.hidden = false;
  }

  #hidePageLoader() {
    this.#pageLoaderEl?.remove();
    // The launcher renders its own overlay under the same contract; a bfcache
    // restore can land with that one still in the DOM and no launcher state to
    // remove it, so clear any stray overlay too.
    document.querySelectorAll('.b2b-plus-page-loader[data-page-loader]')
      .forEach((el) => el.remove());
  }


  /**
   * Open the custom B2B checkout popup for a validated checkout. ALL buyer types
   * review their order here now; the popup itself decides what "Create Order" does
   * (SR on-behalf → /orders/create; normal B2B / rep-as-self → native /checkout).
   *
   * `ensureOrderContext()` is awaited first so the on-behalf context is populated
   * before the checkout element reads it, even if the page-load fetch is still in
   * flight.
   */
  async #proceedToCheckout() {
    try {
      await ensureOrderContext();
    } catch (err) {
      // A context-fetch failure means no active impersonation — the checkout
      // element falls back to native-redirect mode rather than blocking the buyer.
      console.warn('b2b-plus: order-context check before checkout failed', err);
    }

    // Everyone now reviews in the custom B2B checkout popup. What "Create Order"
    // does inside it differs by buyer type: a sales rep ordering ON BEHALF OF a
    // customer places via /orders/create; normal B2B buyers and reps shopping as
    // themselves are redirected to native Shopify /checkout (decided in the
    // checkout element via isSalesRepOnBehalf()).
    await this.#openCheckoutPopup();
  }

  /**
   * Send the buyer to the full-page B2B checkout.
   *
   * The fallback for every case where the modal cannot open. It is a real fallback, not
   * an error path: `sections/b2b-plus-checkout.liquid` still renders the identical
   * checkout at `config.checkout.globalModal.fallbackUrl`, so the buyer completes their
   * order — they just lose the popup. Returns false when no fallback is configured, so
   * the caller can say something rather than leaving the CTA dead.
   *
   * @param {string} why - Logged, not shown.
   * @returns {boolean} Whether a navigation was started.
   */
  #navigateToCheckoutPage(why) {
    const url = window.b2bPlus?.config?.checkout?.globalModal?.fallbackUrl ?? '';
    if (!url) {
      console.error(`b2b-plus: ${why}, and no checkout.globalModal.fallbackUrl is configured.`);
      toastNotification(
        b2bPlusString('checkoutUnavailable') || 'Checkout is unavailable. Please try again.',
        'error',
      );
      return false;
    }
    console.warn(`b2b-plus: ${why} — falling back to the full-page checkout at ${url}.`);
    window.location.href = url;
    return true;
  }

  /**
   * Open the checkout popup through the global launcher.
   *
   * The modal markup (snippets/b2b-plus-checkout-modal.liquid, rendered globally
   * from layout/theme.liquid) and every mechanic of opening it — loader, ready
   * handshake, close routine, focus handling — live in
   * `window.b2bPlus.checkout` (assets/b2b-plus-checkout-launcher.js). This method
   * only supplies the cart page's two extra concerns: unlocking the cart so the
   * buyer can return to editing, and re-hydrating the cart on close.
   *
   * Every way this can fail ends in a navigation to the full-page checkout rather than a
   * console message. An earlier version logged and returned, so with the modal disabled —
   * or the launcher simply not registered — clicking Checkout did NOTHING AT ALL.
   */
  async #openCheckoutPopup() {
    const launcher = window.b2bPlus?.checkout;
    const disabled = window.b2bPlus?.config?.checkout?.globalModal?.enabled === false;

    // Decided BEFORE unlocking the cart: on the fallback path we are navigating away, and
    // leaving the cart unlocked on the way out would flash an editable cart over a page
    // that is already being replaced.
    if (disabled || !launcher?.open) {
      const why = disabled
        ? 'the global checkout modal is disabled (config.checkout.globalModal.enabled)'
        : 'window.b2bPlus.checkout.open is unavailable';
      if (this.#navigateToCheckoutPage(why)) return;
      // No fallback configured — the toast has been shown; restore the CTA so the buyer
      // isn't left staring at a spinner.
      this.#cartFooterEl?.setCheckoutProgress?.('idle');
      this.#cartFooterEl?.syncCheckoutCtaFromTotals?.();
      return;
    }

    this.#setCartLocked(false);
    this.#cartFooterEl?.setCheckoutProgress?.('idle');
    this.#cartFooterEl?.syncCheckoutCtaFromTotals?.();

    try {
      // `onClose` runs while the launcher's full-page loader is still up, so the
      // buyer never sees a stale cart between closing the popup and the re-read.
      await launcher.open({ onClose: () => this.#loadCartItems() });
    } catch (err) {
      // The launcher has already toasted whatever went wrong (load failure, a modal
      // already open, a context mismatch). Don't navigate on top of that — a second
      // attempt at the same checkout is the buyer's call, not ours.
      console.error('b2b-plus: opening the checkout popup failed', err);
    }
  }

  // ── PO number field ───────────────────────────────────────────────────────
  // Behavior lives here on the cart element; the PURE helpers (snapshot reads,
  // sync-write computation, record walk) live in b2b-plus/cart-sdk and are
  // shared with the checkout/flatten path. Gated on isPoNumberEnabled().

  /**
   * Sync the PO input's displayed value with the latest `#cartData` without
   * re-rendering the field. No-ops when "All Dates" is active (window scope) or
   * while the user is focused on the input.
   */
  #refreshPoBoxValue() {
    if (!isPoNumberEnabled()) return;
    const input = this.#poBoxEl?.querySelector('[data-po-number-input]');
    if (!input) return;
    // Don't overwrite text the user is currently typing.
    if (document.activeElement === input) return;

    if (poNumberScope() === 'global') {
      input.value = getGlobalPoFromSnapshot(this.#cartData);
      return;
    }
    const date = this.#activeFilters?.date ?? 'all';
    if (date === 'all') return;
    input.value = getPoFromSnapshot(this.#cartData, date);
  }

  /**
   * Show/hide + (re)render the PO field for the active date tab. Global scope
   * shows one input whenever the cart is non-empty; window scope shows a
   * per-window input unless "all" is active.
   * @param {string} dateWindow - Active tab value ("all" or "YYYY-MM-DD to YYYY-MM-DD").
   */
  #updatePoBox(dateWindow) {
    const el = this.#poBoxEl;
    if (!el) return;
    if (!isPoNumberEnabled()) { el.hidden = true; el.innerHTML = ''; return; }

    if (poNumberScope() === 'global') {
      const hasCart = this.#cartData && Object.keys(this.#cartData).length > 0;
      if (!hasCart) { el.hidden = true; el.innerHTML = ''; return; }
      el.hidden = false;
      const existing = el.querySelector('[data-po-number-input]');
      if (existing && existing.dataset.poScope === 'global') return;
      this.#renderPoBoxField(el, { scope: 'global', currentValue: getGlobalPoFromSnapshot(this.#cartData) });
      return;
    }

    if (dateWindow === 'all') { el.hidden = true; el.innerHTML = ''; return; }
    el.hidden = false;
    // If the input already targets this exact window, leave the user's text alone.
    const existing = el.querySelector('[data-po-number-input]');
    if (existing && existing.dataset.poDateWindow === dateWindow) return;
    this.#renderPoBoxField(el, { scope: 'window', dateWindow, currentValue: getPoFromSnapshot(this.#cartData, dateWindow) });
  }

  /**
   * Build the "#PO" prefix + text input into the box. One renderer for both
   * scopes (was two near-identical methods); they differ only in the dataset
   * marker and which save predicate the debounced input commits.
   * @param {Element} el
   * @param {{ scope: 'global'|'window', dateWindow?: string, currentValue: string }} opts
   */
  #renderPoBoxField(el, { scope, dateWindow, currentValue }) {
    el.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'b2b-plus-po-field-wrapper';

    const prefix = document.createElement('span');
    prefix.className = 'b2b-plus-po-prefix';
    prefix.textContent = '#PO';

    const input = document.createElement('input');
    input.type        = 'text';
    input.className   = 'b2b-plus-po-input';
    input.placeholder = 'Enter PO number';
    input.setAttribute('data-po-number-input', '');
    if (scope === 'global') input.dataset.poScope = 'global';
    else                    input.dataset.poDateWindow = dateWindow;
    input.value = currentValue;

    wrapper.appendChild(prefix);
    wrapper.appendChild(input);
    el.appendChild(wrapper);

    let saveTimer = null;
    input.addEventListener('input', () => {
      clearTimeout(saveTimer);
      saveTimer = setTimeout(() => {
        const value = input.value.trim();
        if (scope === 'global') {
          if (poNumberScope() === 'global') this.#persistPO(value, () => true);
        } else {
          const target = String(input.dataset.poDateWindow ?? '').trim();
          if (target) this.#persistPO(value, (row) => String(row.deliveryWindow ?? '').trim() === target);
        }
      }, 500);
    });
  }

  /**
   * Persist `value` into every record row matched by `predicate` — one IDB
   * read/write per handle — then re-read the snapshot when anything changed.
   * Unifies the former #savePONumber / #saveGlobalPONumber via the pure
   * `applyPoToRecord` record-walk in cart-sdk.
   * @param {string} value
   * @param {(row: object) => boolean} predicate
   */
  async #persistPO(value, predicate) {
    if (!isPoNumberEnabled() || !this.#cartData) return;
    const handles = Object.keys(this.#cartData);
    const mutated = await Promise.all(
      handles.map(async (handle) => {
        const rec = await getProductRecord(handle);
        if (!rec || typeof rec !== 'object' || !Object.keys(rec).length) return false;
        const changed = applyPoToRecord(rec, value, predicate);
        if (changed) await saveProductRecord(handle, rec);
        return changed;
      }),
    );
    if (mutated.some(Boolean)) this.#cartData = await getCart();
  }

  /**
   * Prebook / prebooks / booking / bookings rows are not capped by ADPI availability (often absent or blank).
   * Must match {@link B2BPlusProductTable}'s `#isPrebookOrderType` normalisation.
   * @param {string} [type] - Cart line `order type` property.
   * @returns {boolean}
   */
  async #checkFreshInventory() {
    const { strings: s } = window.b2bPlus;
    const errorsEl         = this.#cartErrorsEl;
    const validProducts    = this.#validProducts ?? [];
    const productIds       = validProducts.map((p) => p.id).filter(Boolean);

    if (!productIds.length || this.#cartData === null) return true;

    const cartData = await getCart();

    // Fail CLOSED: an inventory-gated B2B order must never check out when stock
    // cannot be verified. A network/API failure blocks checkout and surfaces a
    // retryable error rather than silently proceeding (which risks overselling).
    const showInventoryUnavailable = () => {
      const msg = s.cartCheckoutInventoryUnavailable
        || s.cartCheckoutError
        || 'Could not verify inventory. Please try again in a moment.';
      this.#setBanner('checkout-inventory-unavailable', cartMessageWithHandlesTemplate(msg, []));
      errorsEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    let freshResponse;
    try {
      freshResponse = await getInventory(productIds);
    } catch (err) {
      console.error('b2b-plus: inventory check failed during checkout', err);
      showInventoryUnavailable();
      return false;
    }
    if (!freshResponse?.success || !freshResponse?.data) {
      showInventoryUnavailable();
      return false;
    }

    const { exceeded, problemHandles, invalidItems } = findInventoryExceededIssues({
      cartData,
      products:           validProducts,
      freshInventoryData: freshResponse.data,
    });

    // Push the fresh availability into the rendered tables so the displayed
    // "available" numbers + per-input caps reflect the latest stock, and any
    // over-stock input is highlighted inline — not just listed in the banner.
    this.#applyFreshAvailabilityToTables(freshResponse.data);

    if (!exceeded || !problemHandles.length) return true;

    // Each problem line becomes an issue on the strip and is pinned to its cell: the
    // descriptor carries the variant and inventory window (what tells two lines of one
    // SKU apart), `code` the category and `message` the requested/available reason.
    // The caller scrolls to the first one.
    this.#setIssueSource('inventory', invalidItems.map((item) => ({
      ...item,
      code: item.code ?? 'out-of-stock',
    })));
    return false;
  }

  /**
   * Re-apply fresh inventory availability to every rendered product table so the
   * displayed caps/badges update and over-stock inputs highlight. Each table reads
   * only its own variants from the batched inventory payload.
   *
   * @param {object} freshInventoryData - raw inventory API `data` object
   */
  #applyFreshAvailabilityToTables(freshInventoryData) {
    const container = this.#cartItemsContainer;
    if (!container || !freshInventoryData) return;
    container.querySelectorAll('b2b-plus-product-table').forEach((table) => {
      try {
        table.refreshAvailabilityFromInventory?.(freshInventoryData);
      } catch (err) {
        console.warn('b2b-plus: refreshAvailabilityFromInventory failed', err);
      }
    });
  }

  /**
   * Backfill `#inventoryData` when the cart list is non-empty but inventory was never loaded
   * (e.g. empty-cart page load). Quick-add can merge one chunk in `#prependProductTable`;
   * quantity saves only run `#refreshCartSummary`, which relies on this for multi-apply bounds.
   * @returns {Promise<boolean>} true if inventory was fetched this call
   */
  async #ensureCartInventoryLoaded() {
    const validProducts = this.#validProducts ?? [];
    if (!validProducts.length || this.#inventoryData) return false;

    const productIds = validProducts.map((p) => p.id).filter(Boolean);
    if (!productIds.length) return false;

    let response;
    try {
      response = await getInventory(productIds);
    } catch {
      return false;
    }
    const data = response?.success && response?.data ? response.data : null;
    if (!data) return false;

    this.#inventoryData = data;
    this.#inventoryFilterOptions = getInventoryFilterData(data);
    return true;
  }

  /**
   * Re-read IndexedDB and refresh header date tabs + footer totals after a
   * product-table auto-save. Serializes overlapping calls (qty keystrokes can
   * emit multiple `b2b:cart-saved` events while a prior refresh is still
   * awaiting IDB) so a stale run cannot clear `updating--data` while a newer
   * save still needs a refresh — and so the overlay cannot stick forever when
   * a refresh throws.
   * @returns {Promise<void>}
   */
  async #refreshCartSummary() {
    if (this.#refreshSummaryInFlight) {
      this.#refreshSummaryQueued = true;
      return this.#refreshSummaryInFlight;
    }
    this.#refreshSummaryInFlight = this.#runRefreshCartSummary();
    try {
      await this.#refreshSummaryInFlight;
    } finally {
      this.#refreshSummaryInFlight = null;
      if (this.#refreshSummaryQueued) {
        this.#refreshSummaryQueued = false;
        return this.#refreshCartSummary();
      }
    }
  }

  async #runRefreshCartSummary() {
    try {
      // If there are no products left (last item was just removed), zero the
      // footer immediately without hitting IndexedDB — avoids a stale IDB read
      // winning a race against the synchronous zeroing done by #removeProductTable.
      if (!this.#validProducts?.length) {
        this.#cartFooterEl?.init({ totalUnits: 0, totalProducts: 0, subtotal: 0, cartProductTableCount: 0 });
        this.#cartHeaderEl?.init();
        this.#updatePoBox('all');
        // No items left → no shipments; drop every stored shipping selection so a
        // stale window entry can't leak a phantom shipping line at order-create.
        clearCheckoutShipmentShipping()
          .catch((err) => console.error('b2b-plus-cart: clear shipment shipping (empty cart) failed', err));
        return;
      }

      const fetchedInventory = await this.#ensureCartInventoryLoaded();

      // Snapshot the previous cart state before overwriting so we can detect
      // delivery-window changes below.
      const prevData  = this.#cartData;
      const freshData = await getCart();

      // Guard: getCart() returns null when the IndexedDB read fails silently
      // (e.g. private browsing, quota exceeded). Falling back to the last
      // known-good snapshot keeps footer totals accurate while products are
      // still visible in the DOM, instead of silently dropping to 0.
      const isValidData = freshData !== null && typeof freshData === 'object';
      if (!isValidData) {
        console.warn('b2b-plus: refreshCartSummary received null from getCart(); using last cached cart snapshot for totals');
      } else {
        this.#cartData = freshData;
      }
      const cartData = isValidData ? freshData : (this.#cartData ?? {});

      // ── Sync po_numbers: restore wiped POs (same window) + clear stale POs (window changed) ──
      if (isValidData && prevData && isPoNumberEnabled()) {
        const { toWrite } = poNumberScope() === 'global'
          ? computePoSyncWritesGlobal(prevData, freshData)
          : computePoSyncWrites(prevData, freshData);
        if (toWrite.length) await addToCart(toWrite);
      }

      // ── Invalidate stale shipping selections for any changed shipment ──
      // A shipment's shipping rate was priced against the exact line contents +
      // quantities of its delivery window. If the buyer edits a quantity, adds/
      // removes a variant, or moves items across windows, the stored rate for
      // that window is no longer valid, so drop it from IndexedDB. The buyer
      // re-selects a rate on the checkout shipment card.
      if (isValidData && prevData) {
        const changedWindows = computeChangedShippingWindows(prevData, freshData);
        if (changedWindows.size) {
          await Promise.all(
            [...changedWindows].map((win) =>
              saveShipmentShippingForWindow(win, null)
                .catch((err) => console.error('b2b-plus-cart: clear shipment shipping failed', win, err)),
            ),
          );
        }
      }

      // Refresh header — rebuild delivery window tabs from updated cart.
      const selectedDeliveryWindows = await this.#collectDeliveryWindows(cartData);

      const currentTabs = new Set(
        [...(this.#cartHeaderEl?.querySelectorAll('[data-tab]:not([data-tab="all"])') ?? [])]
          .map((btn) => btn.dataset.tab),
      );
      const windowsChanged = deliveryWindowsChanged(currentTabs, selectedDeliveryWindows);

      const { config: cfg } = window.b2bPlus;
      const invDateRange = buildInventoryDateRange(
        getInventoryDateRange(this.#inventoryData),
        { enableMultiApply: cfg?.enableMultiApply },
        { toISODate },
      );
      const hasMultiApplyUi = !!this.#cartHeaderEl?.querySelector('[data-multi-apply-date]');
      const needsMultiApplyChrome = !!invDateRange && !hasMultiApplyUi;
      const shipmentTotals = computeShipmentTotals(this.#validProducts, cartData, displayedUnitCents);

      if (windowsChanged || needsMultiApplyChrome) {
        // Full re-init so lit-html owns every render — avoids partial DOM mutations
        // (insertBefore, innerHTML) that conflict with lit-html's reconciliation.
        this.#cartHeaderEl?.init({
          dates:             selectedDeliveryWindows,
          seasons:           this.#inventoryFilterOptions?.seasons ?? [],
          types:             this.#inventoryFilterOptions?.types   ?? [],
          inventoryDateRange: invDateRange,
          shipmentTotals,
        });
        // Header rebuilt with "All Dates" active — sync cart rows to match.
        const f = this.#activeFilters ?? { date: 'all', season: 'all', type: 'all' };
        this.#applyFilters({ ...f, date: 'all' });
      } else {
        // Windows unchanged — refresh tab totals without resetting the active tab.
        this.#cartHeaderEl?.updateShipmentTotals(shipmentTotals);
      }

      if (fetchedInventory) this.#syncCartBulkApplyOnTables();

      // Refresh footer — recompute totals from the full validProducts list so
      // the displayed numbers always reflect the entire cart, not just the
      // currently rendered page.
      this.#cartFooterEl?.init({
        ...computeCartTotals(this.#validProducts, cartData, displayedUnitCents),
        shipmentTotals,
      });
      this.#cartFooterEl?.enable({
        beforeCheckout:  () => this.#validateCheckout(),
        onCheckoutError: () => this.#setCartLocked(false),
        onCheckoutReady: () => this.#proceedToCheckout(),
      });

      // Keep the PO field in sync with the (possibly updated) cart data.
      // This is a no-op when "All Dates" is active or the user is typing.
      this.#refreshPoBoxValue();
    } catch (err) {
      console.error('b2b-plus-cart: refreshCartSummary failed', err);
    } finally {
      // Always drop the dim/inert overlay — including early empty-cart return,
      // thrown errors, and partial failures — so qty → date edits cannot leave
      // the product table permanently unresponsive (`pointer-events: none`).
      this.#clearUpdatingDataClass();
    }
  }

  // ── Shared helpers ────────────────────────────────────────────────────────

  /**
   * Label for the “similar sizes” bulk option for a given product.
   * Falls back to the first cart product when no productJSON is supplied.
   * @param {object|null} [productJSON]
   * @returns {string}
   */
  #cartSimilarSizeOptionLabel(productJSON = null) {
    const p = productJSON ?? this.#validProducts?.[0];
    if (!p?.options?.length) return b2bPlusString('bulkApplySizeOptionFallback');
    return optionLabel(p.options[inferSizeOptionIndex(p)]) || b2bPlusString('bulkApplySizeOptionFallback');
  }


  /**
   * Returns true when `productJSON` has the configured split option with two or more distinct values.
   * Used to decide whether quick-add should fall back to a full cart reload.
   * @param {object|null|undefined} productJSON
   * @returns {boolean}
   */
  #hasSplitOptionGroups(productJSON) {
    const splitOption = String(window.b2bPlus?.config?.productTableSplitOption ?? '').trim();
    return hasSplitOptionGroups(productJSON, splitOption, { optionLabel });
  }

  /**
   * Writes bulk-apply dataset attributes on one `<b2b-plus-product-table>` from the current cart.
   * @param {HTMLElement} tableEl
   */
  #hydrateCartBulkApplyDataset(tableEl) {
    const bulkOn = window.b2bPlus?.config?.cart?.enableBulkQuantityApply !== false;
    const n      = this.#validProducts?.length ?? 0;
    if (!bulkOn || n < 2) {
      tableEl.removeAttribute('data-cart-bulk-apply-controls');
      tableEl.removeAttribute('data-cart-bulk-departments');
      tableEl.removeAttribute('data-cart-bulk-pairs');
      tableEl.removeAttribute('data-cart-similar-size-label');
      tableEl.removeAttribute('data-cart-bulk-apply-active');
      return;
    }
    // Find the product JSON for this specific table so options are scoped
    // to its own variants only — not pooled across all cart products.
    const handle      = tableEl.dataset.productHandle ?? '';
    const productJSON = this.#validProducts?.find((p) => p.handle === handle) ?? null;

    // Departments: only from this product (not all cart products).
    const deptRaw    = String(productJSON?.metafields?.departments ?? '').trim();
    const departments = deptRaw ? [deptRaw] : [];

    // Type/season pairs: filtered to this product's variant IDs so each line
    // item only shows the groups that exist in its own inventory. The variant→
    // inventory key rule (id → inventory_item_id → sku) is owned by
    // `variantInventoryKeys` and shared with `inventoryLinesForVariant`.
    let pairs = [];
    if (this.#inventoryData?.inventories && productJSON?.variants?.length) {
      const variantKeys = new Set(productJSON.variants.flatMap(variantInventoryKeys));
      const filteredInventories = {};
      for (const [k, lines] of Object.entries(this.#inventoryData.inventories)) {
        if (variantKeys.has(String(k))) filteredInventories[k] = lines;
      }
      pairs = getInventoryTypeSeasonPairs({ ...this.#inventoryData, inventories: filteredInventories });
    }
    const similarLabel = this.#cartSimilarSizeOptionLabel(productJSON);
    tableEl.dataset.cartBulkApplyControls = 'true';
    tableEl.dataset.cartBulkDepartments   = JSON.stringify(departments);
    tableEl.dataset.cartBulkPairs         = JSON.stringify(pairs);
    tableEl.dataset.cartSimilarSizeLabel  = similarLabel;
  }

  /**
   * Sync bulk-apply metadata on every rendered product table and refresh each table’s in-shadow UI.
   */
  #syncCartBulkApplyOnTables() {
    const connected = this.#filterTableCache.filter((t) => t.isConnected);
    const visible   = connected.filter((t) => !t.hidden);
    const active    = visible.length >= 2;
    for (const table of connected) {
      this.#hydrateCartBulkApplyDataset(table);
      if (table.dataset.cartBulkApplyControls === 'true') {
        table.dataset.cartBulkApplyActive = active ? 'true' : 'false';
      }
      table.syncCartBulkApplyFromHost?.();
    }
  }

  /**
   * Apply selected bulk scope from one line-item table across all OTHER visible cart tables.
   * Source is always the table whose Apply button was pressed — no fallback to other tables.
   * @param {{ detail: { m: string, t?: string, s?: string, d?: string }, sourceTable: HTMLElement }} payload
   */
  async #onCartBulkApplyFromTable(payload) {
    const { detail, sourceTable } = payload ?? {};
    if (!detail || !sourceTable) return;

    const snapshot =
      typeof sourceTable.getBulkSourceSnapshot === 'function'
        ? sourceTable.getBulkSourceSnapshot(detail)
        : null;
    if (!snapshot) {
      toastNotification(b2bPlusString('bulkApplyNoSource'), 'error');
      return;
    }

    // Tables are paginated in via IntersectionObserver, so only the scrolled-into-view
    // subset lives in #filterTableCache. A cart-wide bulk apply must reach EVERY line
    // item, so force-render any remaining batches and wait for those tables to finish
    // their first render before reading/writing their rows.
    await this.#renderAllRemainingTables();

    const tables = [...this.#filterTableCache].filter((t) => t.isConnected && !t.hidden && t !== sourceTable);
    let anyUpdated = false;
    let anyClamp   = false;
    let anySibling = false;
    for (const table of tables) {
      const r =
        typeof table.applyCartBulkSnapshot === 'function' ? table.applyCartBulkSnapshot(snapshot, detail) : null;
      if (r?.updated) anyUpdated = true;
      if (r?.clamped) anyClamp = true;
      if (r?.siblingMutated) anySibling = true;
    }

    if (!anyUpdated) {
      toastNotification(b2bPlusString('bulkApplyCartNoTargets'), 'info');
      return;
    }

    // In manual-save mode (config.cart.autoUpdateQuantities === false) edits
    // normally stage behind each table's "Save changes" bar. A cart-wide bulk
    // apply dirties many tables at once, so persist them all immediately instead
    // of leaving the buyer to hunt down every affected table's Save button — this
    // matches the auto-save behaviour the same action has when auto-update is on.
    // The source table is flushed too so its staged quantities (the snapshot's
    // origin) commit alongside the propagated ones. flushPendingSave is a no-op
    // on tables that aren't dirty.
    if (window.b2bPlus?.config?.cart?.autoUpdateQuantities === false) {
      await Promise.all(
        [sourceTable, ...tables].map((t) => {
          try { return t.flushPendingSave?.() ?? Promise.resolve(); }
          catch { return Promise.resolve(); }
        }),
      );
    }

    if (anyClamp || anySibling) {
      toastNotification(b2bPlusString('bulkApplySuccessPartial'), 'info');
    } else {
      toastNotification(b2bPlusString('bulkApplySuccess'), 'success');
    }
  }

  /**
   * Build a configured <b2b-plus-product-table> element ready to append.
   * @param {object}      productJSON
   * @param {object|null} inventoryData
   * @param {object[]}    cartItems
   * @returns {HTMLElement}
   */
  #createProductTableElement(productJSON, inventoryData, cartItems) {
    const tableEl = document.createElement('b2b-plus-product-table');
    tableEl.dataset.isCart              = 'true';
    tableEl.dataset.showProductDetails  = 'true';
    tableEl.dataset.showInventoryFilter = 'false';
    tableEl.dataset.showActionButton    = 'false';
    tableEl.setAttribute('data-no-shadow', '');
    tableEl.setAttribute('data-auto-save', '');
    tableEl.dataset.productHandle = productJSON.handle;
    this.#hydrateCartBulkApplyDataset(tableEl);

    // Enable the "Remove Item" button per product table on the cart page.
    // Configurable via config.cart.enableRemoveButton (default true).
    if (window.b2bPlus.config.cart?.enableRemoveButton !== false) {
      tableEl.dataset.showRemoveButton = 'true';
    }

    // cartRecord is not passed here — initializeProductData() reads it directly from IDB,
    // which is always authoritative on the cart page.
    tableEl.setPrefetchedData({ productJSON, inventoryData });

    const scriptEl = document.createElement('script');
    scriptEl.type  = 'application/json';
    scriptEl.setAttribute('data-product-json', '');
    scriptEl.textContent = JSON.stringify(productJSON);
    tableEl.appendChild(scriptEl);

    return tableEl;
  }

  /**
   * Collect all delivery windows present in a cart snapshot into a Set.
   * Pure windowing logic lives in {@link collectDeliveryWindowsFromCart}; this wrapper
   * adds the side-effecting bit: when `showDateFiltersForZeroQty` is explicitly
   * `true`, it also merges windows from all IDB records so pre-configured date tabs
   * remain visible at zero qty. Default (and config value) is `false` — tabs only
   * for windows with at least one unit.
   * @param {object}   cartData       - Handle-keyed cart snapshot (handle → cartItem[]).
   * @param {object[]} [extraItems]   - Additional items not yet reflected in cartData (e.g. quick-add).
   * @returns {Promise<Set<string>>}
   */
  async #collectDeliveryWindows(cartData, extraItems = []) {
    const windows = collectDeliveryWindowsFromCart(cartData, extraItems);
    if (window.b2bPlus?.config?.cart?.showDateFiltersForZeroQty === true) {
      const idbWindows = await getAllStoredDeliveryWindows();
      idbWindows.forEach((w) => windows.add(w));
    }
    return windows;
  }

  /**
   * Prepend a single product table to the top of the cart items list.
   * Replaces any existing table for the same handle and refreshes totals.
   * Called when quick-add successfully writes to IndexedDB.
   * @param {object}      productJSON
   * @param {object|null} inventoryData
   * @param {object[]}    cartItems
   */
  async #prependProductTable(productJSON, inventoryData, cartItems) {
    const container = this.#cartItemsContainer;
    if (!container) return;
    await enrichProductJSONWithSeasonPrices(productJSON);

    // Remove empty-state placeholder if cart was previously empty.
    container.querySelector('[data-b2b-plus-empty-state]')?.remove();

    // Remove existing table for this handle so we don't duplicate it.
    const existingTable = container.querySelector(
      `b2b-plus-product-table[data-product-handle="${CSS.escape(productJSON.handle)}"]`,
    );
    if (existingTable) {
      const slot = existingTable.closest('[data-b2b-plus-cart-line-slot]');
      (slot ?? existingTable).remove();
      const cIdx = this.#filterTableCache.indexOf(existingTable);
      if (cIdx >= 0) this.#filterTableCache.splice(cIdx, 1);
      this.#filterRowCache.delete(existingTable);
    }

    // Prepend the new table at the top and register it in the filter caches.
    const tableEl = this.#createProductTableElement(productJSON, inventoryData, cartItems);
    this.#appendCartProductLine(container, tableEl, { prepend: true });
    this.#filterTableCache.unshift(tableEl);
    const issues = this.#issuesForHandle(productJSON.handle);
    if (issues.length) tableEl.setCartIssues?.(issues);

    // Keep #validProducts in sync: move/insert this product at the front so
    // #refreshCartSummary always covers it, and adjust #loadedOffset so the
    // IntersectionObserver pagination continues from the correct position.
    if (!this.#validProducts) this.#validProducts = [];
    const existingIdx = this.#validProducts.findIndex((p) => p.handle === productJSON.handle);
    if (existingIdx >= 0) {
      this.#validProducts.splice(existingIdx, 1);
      // Product was already tracked; rendered count stays the same.
    } else {
      // Brand-new product not yet in the list — count it as rendered.
      this.#loadedOffset = Math.min(this.#loadedOffset + 1, this.#validProducts.length + 1);
    }
    this.#validProducts.unshift(productJSON);

    // Merge quick-add inventory into host state. An empty cart skips the inventory fetch in
    // #loadCartItemsInner, so #inventoryData stays null — without this, multi-apply date bounds
    // and filter dropdowns stay missing until a full page reload.
    if (inventoryData) {
      if (!this.#inventoryData) {
        this.#inventoryData = inventoryData;
      } else {
        Object.assign(this.#inventoryData, inventoryData);
      }
      const prevOptions = this.#inventoryFilterOptions;
      const newOptions  = getInventoryFilterData(this.#inventoryData);
      const arraysEqual = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
      const dropdownsStale =
        !prevOptions ||
        !arraysEqual(newOptions.seasons, prevOptions.seasons) ||
        !arraysEqual(newOptions.types,   prevOptions.types);

      if (dropdownsStale) {
        this.#inventoryFilterOptions = newOptions;
        const selectedDeliveryWindows = await this.#collectDeliveryWindows(this.#cartData, cartItems);
        const { config: cfg } = window.b2bPlus;
        const invDateRange2 = buildInventoryDateRange(
          getInventoryDateRange(this.#inventoryData),
          { enableMultiApply: cfg?.enableMultiApply },
          { toISODate },
        );
        this.#cartHeaderEl?.init({
          dates:             selectedDeliveryWindows,
          seasons:           newOptions.seasons,
          types:             newOptions.types,
          inventoryDateRange: invDateRange2,
          shipmentTotals:    computeShipmentTotals(this.#validProducts, this.#cartData, displayedUnitCents),
        });
        this.#applyFilters({ date: 'all', season: 'all', type: 'all' });
      }
    }

    // Refresh date tabs (if delivery windows changed) and footer totals.
    this.#refreshCartSummary();
    if (this.#filterEmptyState) this.#filterEmptyState.hidden = true;
    this.#syncCartBulkApplyOnTables();
    // Prepend-from-quick-add bypasses #loadCartItemsInner, which normally toggles this.
    this.#updateClearCartAction(true);
  }

  /**
   * Remove a single product table from the cart DOM after the user clicks "Remove Item".
   * Updates `#validProducts`, refreshes footer totals, and shows the empty state if needed.
   * @param {string} handle - The product handle that was removed.
   */
  #removeProductTable(handle) {
    const container = this.#cartItemsContainer;
    if (!container) return;
    this.#dropIssues((issue) => issue.handle === handle);

    // Remove the table element from the DOM and evict it from the filter caches.
    const removedTable = container.querySelector(
      `b2b-plus-product-table[data-product-handle="${CSS.escape(handle)}"]`,
    );
    if (removedTable) {
      const slot = removedTable.closest('[data-b2b-plus-cart-line-slot]');
      (slot ?? removedTable).remove();
      const cIdx = this.#filterTableCache.indexOf(removedTable);
      if (cIdx >= 0) this.#filterTableCache.splice(cIdx, 1);
      this.#filterRowCache.delete(removedTable);
    }

    // Drop from the internal product list and adjust the rendered offset.
    const idx = this.#validProducts?.findIndex((p) => p.handle === handle) ?? -1;
    if (idx >= 0) {
      this.#validProducts.splice(idx, 1);
      this.#loadedOffset = Math.max(0, this.#loadedOffset - 1);
    }

    // If the cart is now empty, show the empty state and reset all chrome.
    const remaining = container.querySelectorAll('b2b-plus-product-table');
    if (!remaining.length) {
      // Cancel any pending b2b:cart-saved debounce so its deferred
      // #refreshCartSummary cannot race with us and restore stale totals.
      clearTimeout(this.#cartSavedDebounceTimer);
      this.#cartSavedDebounceTimer = null;
      this.#clearUpdatingDataClass();
      this.#cartFooterEl?.init({ totalUnits: 0, totalProducts: 0, subtotal: 0, cartProductTableCount: 0 });
      this.#cartHeaderEl?.init(); // clears date tabs and filter dropdowns
      this.#activeFilters = null;
      this.#updatePoBox('all');
      if (this.#filterEmptyState) this.#filterEmptyState.hidden = false;
      this.#syncCartBulkApplyOnTables();
      this.#updateClearCartAction(false);
      return;
    }

    // Refresh footer totals from remaining products.
    this.#refreshCartSummary();
    this.#syncCartBulkApplyOnTables();
  }

  #applyFilters({ date, season, type }) {
    // Persist the active filter state so #renderNextBatch can re-apply it
    // to newly appended product tables.
    this.#activeFilters = { date, season, type };

    const { strings } = window.b2bPlus;
    const filterEmptyState = this.#filterEmptyState;

    // ── Phase 1: Read ────────────────────────────────────────────────────
    // #filterTableCache is maintained incrementally by #renderNextBatch /
    // #prependProductTable / #removeProductTable — no querySelectorAll needed.
    //
    // #filterRowCache stores { rows, datePickers } per table, populated lazily
    // on first access.  If a table has no rows yet (still streaming via
    // #appendRowBatches on the child table) we skip caching and retry on the next filter change.
    const rowVis   = new Map(); // <tr>    → visible:boolean
    const tableVis = new Map(); // <table> → visible:boolean

    for (const table of this.#filterTableCache) {
      if (!table.isConnected) continue;
      // Let each table know the active delivery-window filter so its manual-save
      // bar (when config.cart.autoUpdateQuantities is false) can warn that edits
      // are scoped to a single window. No-op when not in manual-save mode.
      table.setActiveDateFilter?.(date);
      // Populate row cache lazily; skip if the table hasn't rendered rows yet.
      let cached = this.#filterRowCache.get(table);
      if (!cached) {
        const rows = [...table.querySelectorAll('[data-b2b-plus-inventory-row][data-row-season]')];
        if (rows.length) {
          cached = {
            rows,
            datePickers: new Map(rows.map((r) => [r, r.querySelector('[data-delivery-date-picker]')])),
          };
          this.#filterRowCache.set(table, cached);
        }
      }

      const rows        = cached?.rows        ?? [];
      const datePickers = cached?.datePickers ?? new Map();

      if (!rows.length) {
        tableVis.set(table, true); // no filter rows yet → treat as visible
        continue;
      }

      let hasVisible = false;
      for (const row of rows) {
        const datePicker = datePickers.get(row);
        const dateWindow = joinDeliveryWindow(datePicker?.dataset.selectedStart, datePicker?.dataset.selectedEnd);

        const visible =
          (date   === 'all' || dateWindow            === date)   &&
          (season === 'all' || row.dataset.rowSeason === season) &&
          (type   === 'all' || row.dataset.rowType   === type);

        rowVis.set(row, visible);
        if (visible) hasVisible = true;
      }

      tableVis.set(table, hasVisible);
    }

    // ── Phase 2: Write ───────────────────────────────────────────────────
    // All visibility decisions are resolved above without any DOM writes, so
    // the browser never needs to flush layout between reads.  Flush the
    // complete set of mutations here in a single pass.
    rowVis.forEach((visible, row) => { row.hidden = !visible; });

    let allTablesHidden = tableVis.size > 0;
    tableVis.forEach((visible, table) => {
      table.hidden = !visible;
      // Grid layout wraps each table in `.b2b-plus-cart-line-slot` with card chrome;
      // hiding only the CE leaves an empty bordered cell.
      const slot = table.closest('[data-b2b-plus-cart-line-slot]');
      if (slot) slot.hidden = !visible;
      if (visible) allTablesHidden = false;
    });

    // Row visibility just changed, so each table's product-details totals
    // ("Total quantity" / "Total price") must be re-scoped to the selected
    // shipment / season / type. Tables own the computation; we only trigger it.
    for (const table of this.#filterTableCache) {
      if (table.isConnected) table.refreshProductLineTotals?.();
    }

    if (filterEmptyState) {
      filterEmptyState.textContent = strings.cartFilterNoMatch;
      filterEmptyState.hidden = !allTablesHidden;
    }

    this.#updatePoBox(date);
    this.#syncCartBulkApplyOnTables();
  }
}

customElements.define('b2b-plus-cart', B2BPlusCart);
